import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, Filter, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { choiceDialog, confirmDialog, promptDialog } from "@/components/ui/app-dialogs";
import { useActiveContext } from "@/lib/active-context";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { useBlueprintDensity } from "@/components/blueprint-backdrop";
import { getStage, type LifecycleStageId } from "@/lib/lifecycle";
import { api, ApiError } from "@/lib/api";
import { useAssignmentGuard } from "@/components/dispatch/assignment-warning-dialog";
import { useDispatchPreflight } from "@/lib/dispatch-preflight";
import { useNewGridSwitch } from "@/lib/new-grid";
import { setGridUiVersion } from "@/lib/api-screen";
import { NewDayBoard } from "./new-grid/day-board";
import { NewGridDayStrip } from "./new-grid/day-strip";
import { isCancelled, isPastDue, stateTally } from "./new-grid/card-state";
import { pickPartsOverrideReason } from "./new-grid/parts-override";
import { live, LIVE_FAST_MS, LIVE_SLOW_MS } from "@/lib/live-query";
import { useLiveDispatch } from "@/lib/live-dispatch";
import { StaleJobDialog, type StaleJobAttempt, type StaleJobBody } from "./stale-job-dialog";
import { HIGHLIGHT_MS, HighlightJobProvider } from "./highlight-job-context";
import { patchJobBody } from "./patchJobBody";

/** One job PATCH from the board. Underscored keys never reach the server. */
interface PatchJobVars {
    jobId: string;
    assignedTo?: string | null;
    scheduledStart?: string | null;
    scheduledEnd?: string | null;
    status?: string;
    force?: boolean;
    forceReason?: string;
    /** Why the office assigned a tech who isn't approved for this job type
     *  (the ask-before-assign prompt). The server writes it into the review
     *  flag ("Office said: …"). */
    assignmentReason?: string;
    /** #1465 step 4: the placement stamp this screen last read; undefined =
     *  look it up, null = no guard, a value = "as of this exact stamp". */
    ifDispatchChangedAt?: string | null;
    /** Lifecycle audit 10/29: the office answered "book anyway" to TECH_OUT. */
    bookWhileOut?: boolean;
    _label?: string;
    _dropTechName?: string;
    _before?: { assigned_to: string | null; scheduled_start: string | null; status: string };
}
import { Button } from "@/components/ui/button";
import { PageSpinner } from "@/components/ui/spinner";
import { addDaysToDateKey, localDateKey } from "@/lib/datetime";
import { isContactStale } from "@/lib/contactFreshness";
import { DaySwimlane } from "./day-swimlane";
import { ScheduleJobDialog, type ScheduleSubmit } from "./schedule-job-dialog";
import { CancelJobDialog } from "@/components/cancel-job-dialog";
import { SkipJobDialog } from "@/components/skip-job-dialog";

import { RouteRail } from "./route-rail";
import { type Cluster, type ClusterJob } from "./demand-panel";
import { CommandBar } from "./command-bar";
import { GridAttentionBar } from "./grid-attention-bar";
import { QueueDock } from "./queue-dock";
import {
    ChipDropPopover,
    HelperBench,
    remainingWorkdays,
    usePairingActions,
    isFullTechPairedToday,
    type ChipDropTarget,
    type HelperChipInfo,
    type PairingRow,
} from "./helper-chips";
import { crewMembers } from "./crew";
import { OutDayDialog, type OutDayPerson } from "./out-day-dialog";
import { OutDayActionsDialog, type OutDayTarget } from "./out-day-actions";
import { buildOutByTech, type OutDayRow } from "./out-days";
import { WeekHeatmap } from "./week-heatmap";
import { RebalanceDialog, type RebalanceResponse } from "./rebalance-dialog";
import { AutoScheduleDialog, type AutoScheduleResponse } from "./auto-schedule-dialog";
import { NudgeCard } from "./nudge-card";
import { GridLegend } from "./grid-legend";
import { type PulseResponse } from "./pulse-types";
import { type MissedJob, type MissedJobsResponse } from "./missed-jobs-section";
import { useLeftOpenList } from "@/components/left-open/left-open-list";
import { TriageAlertBar } from "./triage-alert-bar";
import {
    visitMinutesFromJob,
    MIN_VISIT_MINUTES,
    type DispatchJob,
    type DispatchTech,
    type TechRoute,
    type Trade,
    type ShopLocation,
    TERMINAL_JOB_STATUSES,
    awaitsDispatch,
} from "./dispatch-types";
import { loadGridState, restorableDate, saveGridState } from "./grid-state";
import { cancelledJobPoNotice, type CancelledJobPo } from "@/lib/cancelled-job-pos";
import { useTeachUsed } from "@/teach/teach-state";
import { TeachIntro } from "@/teach/teach-card";
import { TeachDots } from "@/teach/teach-dot";
import { isWoRequired, woRequiredToast } from "@/lib/wo-coming-later";
import { isSiteCheckRequired, siteCheckToast } from "@/lib/site-check";

const DEFAULT_VISIT_MINUTES = 60;
const PULSE_REFRESH_MS = 30_000;

function needsDispatchAttention(job: DispatchJob): boolean {
    if (TERMINAL_JOB_STATUSES.has(job.status)) return false;
    return !job.assigned_to || !job.scheduled_start;
}

interface ShellProps {
    initialMode?: "day" | "week";
}

type FocusMode = "dispatch" | "scheduled" | "live" | "risk" | null;

function contextToFocusMode(ctx: string | null | undefined): FocusMode {
    switch (ctx) {
        case "ready_to_schedule":
            return "dispatch";
        case "scheduled":
            return "scheduled";
        case "in_progress":
            return "live";
        case "at_risk":
            return "risk";
        default:
            return null;
    }
}

const FOCUS_LABELS: Record<NonNullable<FocusMode>, string> = {
    dispatch: "Ready for Dispatch",
    scheduled: "Scheduled",
    live: "In the Field",
    risk: "At Risk",
};

/**
 * A context handed off from another surface (Work Pulse pills, dashboard) only
 * belongs on The Grid if that surface actually routes here. The "In the Field"
 * and "At Risk" pills navigate to the jobs list, not the grid, so their context
 * must NOT linger in sessionStorage and then silently filter the timeline on a
 * later, unrelated grid visit (that empties the swimlane and reads as "all my
 * jobs vanished"). Only the grid-targeting stages (Ready for Dispatch /
 * Scheduled) are adopted from a persisted context; an explicit ?context=
 * deep-link still wins upstream.
 */
function persistedFocusForGrid(mode: LifecycleStageId | "default"): FocusMode {
    if (mode === "default") return null;
    if (getStage(mode).actionRoute !== "/dispatch") return null;
    return contextToFocusMode(mode);
}

export function DispatchShell({ initialMode = "day" }: ShellProps) {
    useBlueprintDensity("open");
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = useAuth();
    // AUTOMATION PRIVACY (G6.1 §8): the whole automation cluster (master
    // toggle, Auto-fill, Suggest moves, missed-job auto-fill) renders only
    // for dispatch.manage holders. The permission arrives in the /me payload.
    const canManageDispatch = can(PERMISSIONS.dispatch.manage);
    // #1766 — "Try the new Grid", per person, off by default.
    const newGrid = useNewGridSwitch();
    // Teach cards (#1838): opening the Grid with the new Grid on is using it.
    useTeachUsed("grid.new", newGrid.on);
    const [searchParams, setSearchParams] = useSearchParams();
    const { context: activeCtx, clearContext } = useActiveContext();

    // G5: the grid remembers its place. Captured once on mount — explicit URL
    // params always win over the remembered state.
    const [savedGridState] = useState(() => loadGridState());

    const [focusMode, setFocusMode] = useState<FocusMode>(() => {
        const urlCtx = searchParams.get("context");
        if (urlCtx) return contextToFocusMode(urlCtx);
        return persistedFocusForGrid(activeCtx.mode);
    });

    useEffect(() => {
        const urlCtx = searchParams.get("context");
        if (urlCtx) {
            setFocusMode(contextToFocusMode(urlCtx));
            const sp = new URLSearchParams(searchParams);
            sp.delete("context");
            setSearchParams(sp, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // The active context is a one-shot hand-off, not a sticky filter. Consume it
    // on mount so it themes the grid at most once (captured above) and can never
    // carry over to a later, unrelated visit. The only persisted contexts that
    // route here are harmless (Ready for Dispatch / Scheduled); anything else
    // (e.g. In the Field, At Risk) is cleared here so it stops following the
    // operator around and silently emptying the timeline.
    useEffect(() => {
        if (activeCtx.mode !== "default") clearContext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mode = (searchParams.get("view") as "day" | "week") ?? savedGridState?.view ?? initialMode;
    const setMode = (next: "day" | "week") => {
        const sp = new URLSearchParams(searchParams);
        sp.set("view", next);
        setSearchParams(sp, { replace: true });
    };

    const initialDateFromQuery = searchParams.get("date");
    // G5: open The Grid where the dispatcher left it. Precedence: explicit
    // ?date= (deep link / Back) → the remembered date, but only when it was
    // saved earlier the SAME local day (a stale date from yesterday's session
    // must not silently pin the board to the past) → today.
    const [selectedDate, setSelectedDate] = useState(
        () => initialDateFromQuery ?? restorableDate(savedGridState) ?? localDateKey(new Date()),
    );

    // Keep ?date= in sync for shareable links and so browser Back re-enters
    // the grid on the same day it was left.
    useEffect(() => {
        const sp = new URLSearchParams(searchParams);
        sp.set("date", selectedDate);
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);
    const [tradeFilter, setTradeFilter] = useState(() => savedGridState?.tradeId ?? "");
    // Day swimlane time window: business hours (configured default) vs. a full
    // 24h view that scrolls. Off-hours jobs surface via the full-day toggle and
    // the swimlane's edge indicators.
    const [fullDayView, setFullDayView] = useState(() => savedGridState?.fullDay ?? false);
    // #975: "Fit day" reframes the board to the hours that actually hold work.
    // Opt-in and remembered, exactly like the full-day toggle beside it.
    const [fitDay, setFitDay] = useState(() => savedGridState?.fitDay ?? false);

    // G5: persist the grid's place on every change so top-nav "THE GRID"
    // (which navigates to bare /dispatch) restores it.
    useEffect(() => {
        saveGridState({ date: selectedDate, view: mode, tradeId: tradeFilter, fullDay: fullDayView, fitDay });
    }, [selectedDate, mode, tradeFilter, fullDayView, fitDay]);

    const nextDay = addDaysToDateKey(selectedDate, 1);

    // #1465 step 4 — the conflict guard's client state. `jobsByIdRef` is the
    // board as this screen last read it (stamps included), read at mutate time.
    const jobsByIdRef = useRef<Map<string, DispatchJob>>(new Map());
    const [staleConflict, setStaleConflict] = useState<{
        body: StaleJobBody;
        vars: PatchJobVars;
        attempt: StaleJobAttempt;
    } | null>(null);
    const [highlightJobId, setHighlightJobId] = useState<string | null>(null);

    // #1465 — push: the server says "your board changed", we re-read it. The
    // 15 s polling below stays as the floor while the stream reconnects.
    const liveDispatch = useLiveDispatch();

    const unscheduledQuery = useQuery({
        queryKey: ["dispatch", "unscheduled", tradeFilter],
        ...live(LIVE_FAST_MS),
        queryFn: () => {
            const params = new URLSearchParams({
                limit: "400",
                status: "draft,scheduled,awaiting_scheduling,in_progress",
                exclude_st: "1",
                exclude_archive: "1",
            });
            if (tradeFilter) params.set("trade_id", tradeFilter);
            return api.get<{ jobs: DispatchJob[]; total: number }>(`/api/jobs?${params}`);
        },
    });

    const scheduledQuery = useQuery({
        queryKey: ["dispatch", "scheduled", selectedDate, tradeFilter],
        ...live(LIVE_FAST_MS),
        queryFn: () => {
            const params = new URLSearchParams({
                date_from: selectedDate,
                date_to: nextDay,
                limit: "500",
                // Issue #1214: keep the historical importers' synthetic
                // record-carrier jobs off the board. They're completed and
                // unassigned with a scheduled_start taken from the test date,
                // so they land on a dispatch day looking like work nobody
                // picked up — 481 of them in one day before this filter.
                exclude_archive: "1",
                exclude_st: "1",
            });
            if (tradeFilter) params.set("trade_id", tradeFilter);
            return api.get<{ jobs: DispatchJob[]; total: number }>(`/api/jobs?${params}`);
        },
    });

    const tradesQuery = useQuery({
        queryKey: ["trades"],
        queryFn: () => api.get<{ trades: Trade[] }>("/api/trades"),
        staleTime: 5 * 60_000,
    });

    const techsQuery = useQuery({
        queryKey: ["dispatch", "technicians"],
        queryFn: () => api.get<{ users: DispatchTech[] }>("/api/dispatch/technicians"),
        staleTime: 5 * 60_000,
    });

    // Helper→lead pairings for the selected day. Drives the helper sticker
    // chips: paired helpers pin to their lead's lane header, unattached
    // helpers sit on the bench strip — helpers never get lanes of their own.
    const pairingsQuery = useQuery({
        queryKey: ["dispatch", "pairings", selectedDate],
        ...live(LIVE_FAST_MS),
        queryFn: () =>
            api.get<{ date: string; pairings: PairingRow[] }>(
                `/api/dispatch/pairings?date=${encodeURIComponent(selectedDate)}`,
            ),
        enabled: mode === "day",
    });

    // #1268 — who is off the board on this date, and why. One row per
    // (person, date) from PTO approvals, call-ins, and days the office marked
    // out for a class. The auto-scheduler has always honoured these; until now
    // nothing on the Grid said so, so a dispatcher looking at a quiet lane
    // couldn't tell "free" from "at school all week".
    const outDaysQuery = useQuery({
        queryKey: ["dispatch", "out-days", selectedDate],
        ...live(LIVE_FAST_MS),
        queryFn: () =>
            api.get<{ days: OutDayRow[] }>(
                `/api/dispatch/out-days?start=${encodeURIComponent(selectedDate)}&end=${encodeURIComponent(selectedDate)}`,
            ),
        enabled: mode === "day",
    });
    const outByTech = useMemo(
        () => buildOutByTech(outDaysQuery.data?.days, selectedDate),
        [outDaysQuery.data, selectedDate],
    );

    const techRoutesQuery = useQuery({
        queryKey: ["dispatch-tech-routes", selectedDate],
        ...live(LIVE_FAST_MS),
        queryFn: () =>
            api.get<{ routes: TechRoute[]; shop?: ShopLocation }>(`/api/dispatch/tech-routes?date=${selectedDate}`),
        enabled: mode === "day",
    });

    const clustersQuery = useQuery({
        queryKey: ["dispatch", "clusters"],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<{ clusters: Cluster[]; totalJobs: number }>("/api/dispatch/clusters"),
    });

    const pulseQuery = useQuery({
        queryKey: ["dispatch", "pulse", selectedDate],
        queryFn: () => api.get<PulseResponse>(`/api/dispatch/pulse?date=${selectedDate}`),
        enabled: mode === "day",
        refetchInterval: PULSE_REFRESH_MS,
    });

    // Missed-jobs feed for the new top-of-grid triage section. Fed by
    // GET /api/dispatch/missed-jobs (derived: scheduled before today,
    // still active, never completed). Drives both the section list and
    // the per-row drag/drop into today's swimlane.
    // No job left open: when the switch is on for this user the Left open list
    // replaces Missed jobs in the triage bar (and its count on the new Grid).
    const leftOpenQuery = useLeftOpenList();
    const leftOpenOn = leftOpenQuery.data?.switchOn === true;
    const missedJobsQuery = useQuery({
        queryKey: ["dispatch", "missed-jobs"],
        ...live(LIVE_FAST_MS),
        queryFn: () => api.get<MissedJobsResponse>("/api/dispatch/missed-jobs"),
        // Refetch whenever any job mutation lands — see patchJobMutation
        // onSuccess invalidations.
    });

    // Tech eligibility lookup. Loaded once and cached for 5 min — drives skill
    // chips on tech rows and the grey-out behaviour while a job is being
    // dragged from the unscheduled rail or another tech.
    const skillsSummaryQuery = useQuery({
        queryKey: ["dispatch", "skills", "summary"],
        queryFn: () =>
            api.get<{
                techs: Array<{ userId: string; skillCodes: string[] }>;
                jobTypes: Array<{
                    jobTypeId: string;
                    jobTypeCode: string;
                    requiredSkillCodes: string[];
                }>;
                techJobTypeApprovals: Array<{
                    userId: string;
                    jobTypeIds: string[];
                }>;
                restrictedCities: Array<{
                    id: string;
                    city: string;
                    region: string;
                    label: string;
                    waterProviderId: string | null;
                }>;
                cityCertifications: Array<{
                    userId: string;
                    restrictedCityIds: string[];
                }>;
            }>("/api/dispatch/skills/summary"),
        staleTime: 5 * 60_000,
    });

    const rebalanceMutation = useMutation({
        mutationFn: () => api.post<RebalanceResponse>("/api/dispatch/rebalance", { date: selectedDate }),
    });
    const [rebalanceOpen, setRebalanceOpen] = useState(false);

    const autoScheduleMutation = useMutation({
        mutationFn: () =>
            api.post<AutoScheduleResponse>("/api/dispatch/auto-schedule", {
                fromDate: selectedDate,
                apply: false,
            }),
    });
    const [autoScheduleOpen, setAutoScheduleOpen] = useState(false);
    const [cancelJobId, setCancelJobId] = useState<string | null>(null);
    const [skipJobId, setSkipJobId] = useState<string | null>(null);

    /* ───── Auto-scheduling master switch ─────────────────────────────────
     * Mirrors the org_settings entry the background runner (in
     * services/dispatchAutoScheduleRunner.ts) reads on every tick. When ON
     * the runner re-plans the unscheduled queue every N minutes; confirmed
     * bookings are protected by the auto-scheduler itself.
     */
    interface AutoScheduleSettings {
        enabled: boolean;
        runIntervalMinutes: number;
        lastRunAt: string | null;
        lastRunSummary: {
            startedAt?: string;
            durationMs?: number;
            proposalsApplied?: number;
            jobsConsidered?: number;
            jobsProposed?: number;
            jobsSkipped?: number;
            error?: string;
        };
    }
    const autoSettingsQuery = useQuery({
        queryKey: ["dispatch", "autoschedule", "settings"],
        queryFn: () => api.get<AutoScheduleSettings>("/api/dispatch/autoschedule/settings"),
        refetchInterval: 60_000,
    });
    const toggleAutoScheduleMutation = useMutation({
        mutationFn: (enabled: boolean) =>
            api.patch<{ ok: true }>("/api/dispatch/autoschedule/settings", {
                enabled,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["dispatch", "autoschedule", "settings"] });
        },
    });
    const autoEnabled = !!autoSettingsQuery.data?.enabled;

    // Cross-component dragging state. Lifted here so a drag that starts in the
    // DemandPanel still drives the swimlane's eligibility highlights, and a
    // drop on the swimlane resets it.
    const [draggingJobId, setDraggingJobId] = useState<string | null>(null);

    const patchJobMutation = useMutation({
        mutationFn: ({
            jobId,
            assignedTo,
            scheduledStart,
            scheduledEnd,
            status,
            force,
            forceReason,
            assignmentReason,
            ifDispatchChangedAt,
            bookWhileOut,
        }: PatchJobVars) => {
            // #1465 step 4 — the conflict guard. A placement change carries the
            // job's stamp as this screen last read it, so the server can refuse
            // a drop onto a job someone else moved in between. `null` opts out
            // (a fresh stamp from the 409 is how "Move it anyway" gets through).
            const touchesPlacement =
                assignedTo !== undefined || scheduledStart !== undefined || scheduledEnd !== undefined;
            const stamp =
                ifDispatchChangedAt !== undefined
                    ? ifDispatchChangedAt
                    : touchesPlacement
                      ? (jobsByIdRef.current.get(jobId)?.dispatch_changed_at ?? null)
                      : null;
            return api.patch(
                `/api/jobs/${jobId}`,
                patchJobBody({
                    assignedTo,
                    scheduledStart,
                    scheduledEnd,
                    status,
                    force,
                    forceReason,
                    assignmentReason,
                    ifDispatchChangedAt: stamp,
                    bookWhileOut,
                }),
            );
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
            qc.invalidateQueries({ queryKey: ["dispatch-map"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job"] });
            qc.invalidateQueries({ queryKey: ["work-pulse"] });
            qc.invalidateQueries({ queryKey: ["dashboard", "command-summary"] });
            qc.invalidateQueries({ queryKey: ["dashboard", "missed-jobs"] });
            // RETURN NEEDED strip clears the moment the job is rescheduled
            // (server derives it from scheduled_start > flag time).
            qc.invalidateQueries({ queryKey: ["dispatch", "return-needed"] });
            if (vars._label) toast.success(vars._label);
        },
        onError: (err, vars) => {
            const action = vars._label ?? "Update job";
            // #1465 step 4 — someone moved this job after our screen last read
            // it. The board has already re-read (push + the invalidation below);
            // the dialog says who, when, and where it is now.
            if (err instanceof ApiError && err.status === 409 && (err.body as any)?.error === "STALE_JOB") {
                const body = err.body as StaleJobBody;
                const before = jobsByIdRef.current.get(vars.jobId);
                qc.invalidateQueries({ queryKey: ["dispatch"] });
                qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
                setStaleConflict({
                    body,
                    vars,
                    attempt: {
                        before: vars._before ?? {
                            assigned_to: before?.assigned_to ?? null,
                            scheduled_start: before?.scheduled_start ?? null,
                            status: before?.status ?? "",
                        },
                        dropTechName: vars._dropTechName ?? null,
                        dropStart: vars.scheduledStart ?? null,
                    },
                });
                return;
            }
            // WO rework slice 7: a work-order customer's job with no WO.
            if (err instanceof ApiError && err.status === 409 && isWoRequired((err.body as any)?.error)) {
                const jobId = (err.body as { jobId?: string }).jobId;
                if (jobId) {
                    woRequiredToast(jobId, (err.body as { message?: string }).message);
                    return;
                }
            }

            // Lifecycle audit 10/29: the person is marked out that day (PTO, a
            // class, a call-in). Ask in plain words; "Book anyway" re-sends
            // the same placement with bookWhileOut.
            if (err instanceof ApiError && err.status === 409 && (err.body as any)?.error === "TECH_OUT") {
                const body = err.body as { message?: string };
                void confirmDialog({
                    title: "They're out that day",
                    message: `${body.message ?? "This person is marked out that day."} Book anyway?`,
                    confirmLabel: "Book anyway",
                    cancelLabel: "Don't book",
                    focusCancel: true,
                }).then((ok) => {
                    if (ok) patchJobMutation.mutate({ ...vars, bookWhileOut: true });
                });
                return;
            }
            // "Check the site" (2026-10-29): intake wasn't sure of the store.
            if (err instanceof ApiError && err.status === 409 && isSiteCheckRequired((err.body as any)?.error)) {
                const body = err.body as { jobId?: string; message?: string };
                siteCheckToast(body.message, () => body.jobId && navigate(`/jobs/${body.jobId}`));
                return;
            }
            // Pre-dispatch payment gate: card-required customer with no card
            // on file. Explain clearly and offer to text/email the secure
            // card-collection link right from the toast.
            if (err instanceof ApiError && err.status === 409 && (err.body as any)?.error === "CARD_REQUIRED") {
                const body = err.body as { message?: string; customerId?: string | null };
                toast.error(body.message ?? "A card on file is required before dispatching this job.", {
                    action: body.customerId
                        ? {
                              label: "Send card link",
                              onClick: () => {
                                  api.post(`/api/customers/${body.customerId}/send-card-link`, {})
                                      .then(() => toast.success("Secure card link sent."))
                                      .catch(() => toast.error("Could not send the card link."));
                              },
                          }
                        : undefined,
                });
                return;
            }
            // Pre-dispatch parts gates: parts not received, or the job is
            // being placed before confirmed delivery + buffer (PARTS_TIMING,
            // the red "parts arrive {date}" chip). Both can be overridden for
            // genuine exceptions, but every override requires a reason —
            // it's ledgered on the blocking PO.
            if (
                err instanceof ApiError &&
                err.status === 409 &&
                ["PARTS_NOT_RECEIVED", "PARTS_TIMING"].includes((err.body as any)?.error)
            ) {
                const body = err.body as { message?: string; error?: string };
                const fallback =
                    body.error === "PARTS_TIMING"
                        ? "Parts arrive after this dispatch date."
                        : "Parts for this job haven't been received yet.";
                toast.error(body.message ?? fallback, {
                    action: {
                        label: "Dispatch anyway",
                        onClick: async () => {
                            // #1766 — on the new Grid the reason is one tap
                            // (the office typed "Dispatch Anyways" 74 times in
                            // four weeks); "Other…" still asks in words.
                            const reason = newGrid.on
                                ? await pickPartsOverrideReason()
                                : await promptDialog({
                                      title: "Override the parts gate",
                                      label: "Reason (required — ledgered on the PO)",
                                      required: true,
                                  });
                            if (reason?.trim()) {
                                patchJobMutation.mutate({ ...vars, force: true, forceReason: reason.trim() });
                            }
                        },
                    },
                });
                return;
            }
            const detail = err instanceof Error && "status" in err ? ` (${(err as any).status})` : "";
            toast.error(`${action} failed${detail} — please try again`);
            console.error("[dispatch patch]", err);
        },
    });

    const allScheduled = scheduledQuery.data?.jobs ?? [];
    const allUnscheduled = useMemo(
        () => (unscheduledQuery.data?.jobs ?? []).filter(needsDispatchAttention),
        [unscheduledQuery.data],
    );
    useEffect(() => {
        const m = new Map<string, DispatchJob>();
        for (const j of scheduledQuery.data?.jobs ?? []) m.set(j.id, j);
        for (const j of unscheduledQuery.data?.jobs ?? []) m.set(j.id, j);
        jobsByIdRef.current = m;
    }, [scheduledQuery.data, unscheduledQuery.data]);

    // "OK, show me" — close, glow the card where the job now is, scroll to it.
    const showMeStale = useCallback(() => {
        const jobId = staleConflict?.vars.jobId ?? null;
        setStaleConflict(null);
        if (!jobId) return;
        setHighlightJobId(jobId);
        window.setTimeout(() => {
            document
                .querySelector(`[data-job-id="${jobId}"]`)
                ?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
        }, 250);
        window.setTimeout(() => setHighlightJobId((cur) => (cur === jobId ? null : cur)), HIGHLIGHT_MS);
    }, [staleConflict]);
    // "Move it anyway" — the same drop, against the job as it is now.
    const moveAnywayStale = useCallback(() => {
        const c = staleConflict;
        setStaleConflict(null);
        if (!c) return;
        patchJobMutation.mutate({ ...c.vars, ifDispatchChangedAt: c.body.changedAt });
    }, [staleConflict, patchJobMutation]);
    const trades = tradesQuery.data?.trades ?? [];
    const techs = techsQuery.data?.users ?? [];

    const techJobMap = useMemo(() => {
        const map = new Map<string, DispatchJob[]>();
        const push = (techId: string, job: DispatchJob) => {
            const list = map.get(techId);
            if (list) {
                if (!list.some((j) => j.id === job.id)) list.push(job);
            } else {
                map.set(techId, [job]);
            }
        };
        for (const tech of techs) map.set(tech.id, []);
        for (const job of allScheduled) {
            if (job.assigned_to) push(job.assigned_to, job);
            // Flexible crew: a multi-tech job renders on EVERY assigned
            // tech's lane (same job object — the crew chip marks the copies;
            // dragging any copy moves the job for the whole crew).
            for (const member of job.crew ?? []) {
                if (member.user_id !== job.assigned_to) push(member.user_id, job);
            }
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
        }
        return map;
    }, [allScheduled, techs]);

    // Helpers NEVER get swim lanes (THE-GRID-NOTES §2). Every helper-eligible
    // user becomes a sticker chip: pinned to their lead's lane header when
    // paired today, or on the Helper bench when solo / unbound. A helper who
    // somehow holds direct job assignments keeps no lane either — their chip
    // gets a ⚠ state ("move them") instead.
    const { gridTechs, helpersByLead, benchHelpers } = useMemo(() => {
        const pairings = pairingsQuery.data?.pairings ?? [];
        const helperIds = new Set<string>();
        const byLead = new Map<string, HelperChipInfo[]>();
        const bench: HelperChipInfo[] = [];
        for (const p of pairings) {
            helperIds.add(p.helperId);
            const info: HelperChipInfo = {
                id: p.helperId,
                name: p.helperName,
                leadId: p.leadId,
                leadName: p.leadName,
                source: p.source,
                soloToday: p.soloToday,
                defaultLeadId: p.defaultLeadId ?? null,
                defaultLeadName: p.defaultLeadName ?? null,
                directJobsCount: techJobMap.get(p.helperId)?.length ?? 0,
                outLabel: outByTech.get(p.helperId)?.label ?? null,
            };
            if (!p.soloToday && p.leadId) {
                const arr = byLead.get(p.leadId) ?? [];
                arr.push(info);
                byLead.set(p.leadId, arr);
            } else {
                bench.push(info);
            }
        }
        const rows = techs.filter((t) => !helperIds.has(t.id));
        return { gridTechs: rows, helpersByLead: byLead, benchHelpers: bench };
    }, [pairingsQuery.data, techs, techJobMap, outByTech]);

    // #1268 — "mark someone out". Opened from a lane header (seeded with that
    // tech) or from the bench label; the dialog's own picker covers helpers,
    // who have no lane to click.
    const [outDialogUserId, setOutDialogUserId] = useState<string | null>(null);
    const [outDialogOpen, setOutDialogOpen] = useState(false);
    const openMarkOut = useCallback((techId?: string) => {
        setOutDialogUserId(techId ?? null);
        setOutDialogOpen(true);
    }, []);
    const outDayPeople = useMemo<OutDayPerson[]>(
        () =>
            techs
                .map((t) => ({
                    id: t.id,
                    name: `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() || t.email,
                    isHelper: Boolean(t.is_field_helper),
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [techs],
    );

    // Click an "Out" chip or lane badge → move or clear that one day, right
    // where the dispatcher is looking (the chip used to be inert).
    const [outActionUserId, setOutActionUserId] = useState<string | null>(null);
    const outActionTarget = useMemo<OutDayTarget | null>(() => {
        if (!outActionUserId) return null;
        const state = outByTech.get(outActionUserId);
        if (!state) return null;
        return {
            userId: outActionUserId,
            name: outDayPeople.find((p) => p.id === outActionUserId)?.name ?? "This person",
            date: selectedDate,
            label: state.label,
            reason: state.reason,
            clearable: state.clearable,
        };
    }, [outActionUserId, outByTech, outDayPeople, selectedDate]);

    // ─── Helper sticker chip drag & drop ────────────────────────────────
    const pairingActions = usePairingActions();
    const [draggingHelperId, setDraggingHelperId] = useState<string | null>(null);
    const [pendingChipDrop, setPendingChipDrop] = useState<ChipDropTarget | null>(null);
    /** V3: the pending drop came from dragging a full TECH's lane name (a
     *  temporary demotion to acting-helper) — hide "make default" in the
     *  popover so a lead never gets a permanent default lead by accident. */
    const [pendingDropIsTech, setPendingDropIsTech] = useState(false);

    const findHelper = useCallback(
        (helperId: string): HelperChipInfo | null => {
            for (const list of helpersByLead.values()) {
                const hit = list.find((h) => h.id === helperId);
                if (hit) return hit;
            }
            return benchHelpers.find((h) => h.id === helperId) ?? null;
        },
        [helpersByLead, benchHelpers],
    );

    const handleHelperDrop = useCallback(
        (helperId: string, targetLeadId: string, x: number, y: number) => {
            setDraggingHelperId(null);
            const helper = findHelper(helperId);
            if (!helper) return;
            // Dropping back on today's current lead is a no-op, not a popover.
            if (!helper.soloToday && helper.leadId === targetLeadId) return;
            const lead = techs.find((t) => t.id === targetLeadId);
            const leadName = lead
                ? `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim() || lead.email || "lead"
                : "lead";
            setPendingDropIsTech(false);
            setPendingChipDrop({ helper, leadId: targetLeadId, leadName, x, y });
        },
        [findHelper, techs],
    );

    // V3: a full tech's name dropped on another lane — pair him as an
    // acting-helper for the chosen scope. His lane folds away for those days
    // (the pairings query treats any override row as helper-for-the-day) and
    // he gets helper-mode restrictions in the field app for those jobs.
    const handleTechDrop = useCallback(
        (techId: string, targetLeadId: string, x: number, y: number) => {
            if (techId === targetLeadId) return;
            const techRow = techs.find((t) => t.id === techId);
            if (!techRow) return;
            const lead = techs.find((t) => t.id === targetLeadId);
            const leadName = lead
                ? `${lead.first_name ?? ""} ${lead.last_name ?? ""}`.trim() || lead.email || "lead"
                : "lead";
            const name = `${techRow.first_name ?? ""} ${techRow.last_name ?? ""}`.trim() || techRow.email || "tech";
            const helper: HelperChipInfo = {
                id: techId,
                name,
                leadId: null,
                leadName: null,
                source: "unbound",
                soloToday: false,
                defaultLeadId: null,
                defaultLeadName: null,
                directJobsCount: techJobMap.get(techId)?.length ?? 0,
            };
            setPendingDropIsTech(true);
            setPendingChipDrop({ helper, leadId: targetLeadId, leadName, x, y });
        },
        [techs, techJobMap],
    );

    const handleChipScope = useCallback(
        (scope: "today" | "week" | "default") => {
            const drop = pendingChipDrop;
            setPendingChipDrop(null);
            if (!drop) return;
            if (scope === "default") {
                pairingActions.makeDefault(drop.helper, drop.leadId, drop.leadName, selectedDate);
                return;
            }
            const dates = scope === "today" ? [selectedDate] : remainingWorkdays(selectedDate);
            pairingActions.setOverride(drop.helper, drop.leadId, drop.leadName, dates);
        },
        [pairingActions, pendingChipDrop, selectedDate],
    );

    const handleBenchDrop = useCallback(
        (helperId: string) => {
            setDraggingHelperId(null);
            const helper = findHelper(helperId);
            if (!helper) return;
            // A full tech who was made an acting helper for today: "work solo"
            // means "give him his own row back". Writing a solo row would keep
            // him a helper all day (any pairing row makes someone a helper —
            // dispatchPairings.ts), so drop today's row instead, like the ✕.
            if (isFullTechPairedToday(helper, techs)) {
                pairingActions.revertToDefault(helper, selectedDate);
                return;
            }
            if (helper.soloToday) return;
            pairingActions.setOverride(helper, null, null, [selectedDate]);
        },
        [findHelper, pairingActions, selectedDate, techs],
    );

    const handleHelperRevert = useCallback(
        (helper: HelperChipInfo) => pairingActions.revertToDefault(helper, selectedDate),
        [pairingActions, selectedDate],
    );

    const unassignedScheduled = useMemo(() => allScheduled.filter(awaitsDispatch), [allScheduled]);

    // H10: surface "how's today going?" at a glance — jobs in the field and the
    // count needing a human (at risk + overdue). Sourced from the live pulse so
    // it matches the triage bar. #761: completed comes from the pulse too, so
    // every scoreboard number shares one source.
    const inProgressCount = pulseQuery.data?.counts.inProgress ?? 0;
    const completedCount = pulseQuery.data?.counts.completed ?? 0;

    // #826: the live pulse only knows about TODAY, so its attention number was
    // rendered under every date — 0 ATTENTION on a future board carrying an
    // 18-day-overdue job. On any other date, attention means "this day's jobs
    // that aren't ready" (same checks the Command Center runs on tomorrow) and
    // comes from /api/dispatch/readiness. That endpoint also carries the
    // assigned-but-untimed jobs a time grid can never draw (audit C4).
    const isTodaySelected = selectedDate === localDateKey(new Date());
    interface DispatchReadiness {
        date: string;
        jobsTotal: number;
        attentionJobs: number;
        blockers: Array<{ jobId: string; kind: string; jobTitle: string; what: string; fixHref: string }>;
        unslotted: Array<{
            jobId: string;
            label: string;
            assignedTo: string;
            techName: string | null;
            status?: string | null;
        }>;
    }
    const readinessQuery = useQuery({
        queryKey: ["dispatch", "readiness", selectedDate],
        queryFn: () => api.get<DispatchReadiness>(`/api/dispatch/readiness?date=${selectedDate}`),
        refetchInterval: 120_000,
    });
    const needsAttentionCount = isTodaySelected
        ? (pulseQuery.data?.counts.atRisk ?? 0) + (pulseQuery.data?.counts.overdue ?? 0)
        : (readinessQuery.data?.attentionJobs ?? 0);
    // Top per-job reasons behind the attention count, previewed in the
    // command bar's attention tooltip (the full list lives in the triage bar).
    const attentionNotes = useMemo(() => {
        if (isTodaySelected) {
            return (pulseQuery.data?.pulses ?? [])
                .filter((p) => p.risk === "at_risk" || p.risk === "overdue")
                .slice(0, 3)
                .map((p) => `${p.title} — ${p.note}`);
        }
        return (readinessQuery.data?.blockers ?? []).slice(0, 3).map((b) => b.what);
    }, [isTodaySelected, pulseQuery.data, readinessQuery.data]);
    // techId -> assigned-but-untimed jobs, for the swimlane's amber chips.
    const unslottedByTech = useMemo(() => {
        const map = new Map<string, { count: number; labels: string[] }>();
        for (const u of readinessQuery.data?.unslotted ?? []) {
            const entry = map.get(u.assignedTo) ?? { count: 0, labels: [] };
            entry.count += 1;
            if (entry.labels.length < 6) entry.labels.push(u.label);
            map.set(u.assignedTo, entry);
        }
        return map;
    }, [readinessQuery.data]);
    // Issue #1214: this used to filter on `!assigned_to` alone, so every
    // COMPLETED job on the selected day fell into the amber "Unassigned Today"
    // alarm box. Both selectors share `awaitsDispatch` now so they can't drift.
    const unassignedToday = useMemo(() => allScheduled.filter(awaitsDispatch), [allScheduled]);
    const missingCoordsCount = useMemo(
        () => allScheduled.filter((j) => j.latitude == null || j.longitude == null).length,
        [allScheduled],
    );
    const techsOnDuty = useMemo(() => {
        let count = 0;
        for (const [, jobs] of techJobMap) {
            if (jobs.length > 0) count++;
        }
        return count;
    }, [techJobMap]);

    // G6.1: triage-bar expansion is lifted here so the command bar's
    // "attention" counter can open the pulse detail. Snaps open on the
    // missed-feed deep link only — #761 retired the attention auto-expand
    // (the slim bar + red counter already carry the signal; the stack of
    // strips it opened is gone).
    const [triageExpanded, setTriageExpanded] = useState(false);
    useEffect(() => {
        if (searchParams.get("section") === "missed") {
            setTriageExpanded(true);
        }
    }, [searchParams]);

    const openTriageDetail = useCallback(() => {
        setTriageExpanded(true);
        // Wait a frame so the expanded region exists before scrolling to it.
        requestAnimationFrame(() => {
            document.getElementById("dispatch-triage-bar")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, []);

    /**
     * Map of jobId -> risk metadata for the current day. Powers the at-risk
     * indicator on each job block in the swimlane.
     */
    const pulseByJob = useMemo(() => {
        const map = new Map<string, { risk: "ok" | "watch" | "at_risk" | "overdue"; note: string }>();
        for (const p of pulseQuery.data?.pulses ?? []) {
            map.set(p.jobId, { risk: p.risk, note: p.note });
        }
        return map;
    }, [pulseQuery.data]);

    /**
     * Per-tech "Suggested adds" badges: count unscheduled cluster jobs whose
     * jurisdiction matches one of the jurisdictions the tech is already
     * routing through today. Uses tech route SITE NAMES via clusters because
     * the tech-routes payload doesn't include jurisdiction directly — we
     * approximate by matching cluster city against route stops' site_name
     * tokens.
     */
    const suggestionsByTech = useMemo(() => {
        const map = new Map<string, { jobCount: number; label: string }>();
        const clusters = clustersQuery.data?.clusters ?? [];
        if (clusters.length === 0) return map;
        const routes = techRoutesQuery.data?.routes ?? [];
        for (const route of routes) {
            const cityTokens = new Set<string>();
            for (const stop of route.jobs) {
                const blob = `${stop.site_name} ${stop.customer_name}`.toLowerCase();
                cityTokens.add(blob);
            }
            let topCluster: Cluster | null = null;
            let topCount = 0;
            for (const cluster of clusters) {
                const city = cluster.city?.toLowerCase() ?? "";
                if (!city) continue;
                let hit = false;
                for (const t of cityTokens) {
                    if (t.includes(city)) {
                        hit = true;
                        break;
                    }
                }
                if (hit && cluster.jobCount > topCount) {
                    topCluster = cluster;
                    topCount = cluster.jobCount;
                }
            }
            if (topCluster) {
                map.set(route.techId, {
                    jobCount: topCount,
                    label: topCluster.jurisdiction ?? topCluster.city ?? topCluster.submissionPlatform ?? "this area",
                });
            }
        }
        return map;
    }, [clustersQuery.data, techRoutesQuery.data]);

    /**
     * Per-tech list of skill codes (active, non-expired). Drives the small
     * skill chips that render under each tech name in the swimlane.
     */
    const techSkills = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const t of skillsSummaryQuery.data?.techs ?? []) {
            map.set(t.userId, t.skillCodes);
        }
        return map;
    }, [skillsSummaryQuery.data]);

    /**
     * Map of jobId -> required skill codes. Built from job_type_id present on
     * each unscheduled / scheduled job. If a job has no job_type assigned we
     * leave it out (= no skill restriction).
     */
    const jobSkillReqs = useMemo(() => {
        const map = new Map<string, string[]>();
        const jtById = new Map<string, string[]>();
        for (const jt of skillsSummaryQuery.data?.jobTypes ?? []) {
            jtById.set(jt.jobTypeId, jt.requiredSkillCodes);
        }
        const all = [...allScheduled, ...allUnscheduled];
        for (const j of all) {
            if (!j.job_type_id) continue;
            const reqs = jtById.get(j.job_type_id);
            if (reqs && reqs.length > 0) map.set(j.id, reqs);
        }
        return map;
    }, [skillsSummaryQuery.data, allScheduled, allUnscheduled]);

    /**
     * Per-tech set of job_type_id values the tech is approved to lead. This is
     * the real lead-eligibility gate (replaces the skill-based check): a job
     * can only be dropped on a tech approved for its job_type.
     */
    const techJobTypeApprovals = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const t of skillsSummaryQuery.data?.techJobTypeApprovals ?? []) {
            map.set(t.userId, new Set(t.jobTypeIds));
        }
        return map;
    }, [skillsSummaryQuery.data]);

    /**
     * Map of jobId -> job_type_id, used to look up which approval a tech needs
     * to lead a given job. Jobs without a job_type have no lead gate.
     */
    const jobJobTypeId = useMemo(() => {
        const map = new Map<string, string>();
        const all = [...allScheduled, ...allUnscheduled];
        for (const j of all) {
            if (j.job_type_id) map.set(j.id, j.job_type_id);
        }
        return map;
    }, [allScheduled, allUnscheduled]);

    /** Map of job_type_id -> short label (its code) for ineligibility tooltips. */
    const jobTypeLabelById = useMemo(() => {
        const map = new Map<string, string>();
        for (const jt of skillsSummaryQuery.data?.jobTypes ?? []) {
            map.set(jt.jobTypeId, jt.jobTypeCode);
        }
        return map;
    }, [skillsSummaryQuery.data]);

    /**
     * Per-tech set of restricted cities they're approved to work in (by
     * city UUID). Drives the "Plano-only-certain-techs" gating on the
     * swimlane: a job whose site falls in a restricted city refuses to be
     * dropped on a tech missing the corresponding cert.
     */
    const techCityCerts = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const t of skillsSummaryQuery.data?.cityCertifications ?? []) {
            map.set(t.userId, new Set(t.restrictedCityIds));
        }
        return map;
    }, [skillsSummaryQuery.data]);

    /**
     * Short label per restricted city (id -> "Plano"). Drives the small
     * green chips that render under each tech name on the swimlane so
     * dispatchers can spot at a glance who's approved where.
     */
    const restrictedCityLabels = useMemo(() => {
        const map = new Map<string, string>();
        for (const c of skillsSummaryQuery.data?.restrictedCities ?? []) {
            map.set(c.id, c.city);
        }
        return map;
    }, [skillsSummaryQuery.data]);

    /**
     * Map of jobId -> { restrictedCityId, label } for jobs whose site
     * sits in a restricted city. Built from the (city, region) on the job
     * row matched against the active registry. Jobs without a known city
     * — or in cities not on the registry — do not appear in this map and
     * therefore have no extra eligibility check.
     */
    const jobCityRestriction = useMemo(() => {
        const map = new Map<string, { id: string; label: string }>();
        const cities = skillsSummaryQuery.data?.restrictedCities ?? [];
        if (cities.length === 0) return map;
        const byKey = new Map<string, { id: string; label: string }>();
        for (const c of cities) {
            byKey.set(`${c.city.trim().toLowerCase()}|${c.region.trim().toUpperCase()}`, {
                id: c.id,
                label: c.label,
            });
        }
        const all = [...allScheduled, ...allUnscheduled];
        for (const j of all) {
            const city = (j.site_city ?? "").trim().toLowerCase();
            if (!city) continue;
            // Region falls back to TX since current sites lack a state column;
            // the auto-scheduler uses the same fallback so the two stay in sync.
            const region = (j.site_region ?? "TX").trim().toUpperCase();
            const hit = byKey.get(`${city}|${region}`);
            if (hit) map.set(j.id, hit);
        }
        return map;
    }, [skillsSummaryQuery.data, allScheduled, allUnscheduled]);

    /**
     * HELPER-MODE V3: jurisdiction registrations. A water provider with any
     * registered techs is "strict" — assigning a job there to a lead who
     * isn't registered draws an amber warning with a one-click "pair as
     * helper today" action (the drop is never blocked; the dispatcher may
     * genuinely intend it).
     */

    /**
     * #1431 — the same warning, moved in FRONT of the assignment.
     *
     * This used to be a toast fired after the drop had already landed
     * (`warnIfUnregisteredLead`), and it only knew about jurisdiction. The
     * guard asks the server, which checks all five gates against both
     * credential stores, and a clean pairing still never shows a dialog.
     *
     * "Pair with X instead" is kept exactly as it was: the office's real way
     * out is to put a registered lead on the job, not to override.
     */
    const { confirmDispatch } = useDispatchPreflight({ alsoWhen: newGrid.on });
    const { askBeforeAssign, assignmentDialog } = useAssignmentGuard({
        onPair: (candidateName, assessment) => {
            const candidate = techs.find((t) => `${t.first_name ?? ""} ${t.last_name ?? ""}`.trim() === candidateName);
            const blocked = techs.find((t) => t.id === assessment.techId);
            if (!candidate || !blocked) return;
            pairingActions.setOverride(
                {
                    id: blocked.id,
                    name: assessment.techName,
                    leadId: null,
                    leadName: null,
                    source: "unbound",
                    soloToday: false,
                    defaultLeadId: null,
                    defaultLeadName: null,
                    directJobsCount: 0,
                },
                candidate.id,
                `${candidate.first_name ?? ""} ${candidate.last_name ?? ""}`.trim(),
                [selectedDate],
            );
        },
    });

    /**
     * Issue #1245 — put a SECOND person on one job without moving it off the
     * first. Job-scoped (unlike the Helper bench, which pairs for the whole
     * day and folds the helper's lane). Role 'tech' + the server's 'full'
     * default: each lead gets full points (Corbin, 11/3/2026).
     */
    const addCrewMutation = useMutation({
        mutationFn: ({ jobId, techId, force }: { jobId: string; techId: string; force?: boolean; _label?: string }) =>
            api.post(`/api/jobs/${jobId}/assignments`, {
                userId: techId,
                role: "tech",
                ...(force && { force: true }),
            }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
            qc.invalidateQueries({ queryKey: ["dispatch-map"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job"] });
            if (vars._label) toast.success(vars._label);
        },
        onError: (err, vars) => {
            const body = err instanceof ApiError ? (err.body as Record<string, unknown> | undefined) : undefined;
            const code = body?.error;
            if (code === "ALREADY_LEAD" || code === "ALREADY_ASSIGNED") {
                toast.info(typeof body?.message === "string" ? body.message : "That tech is already on this job.");
                return;
            }
            // CREW_FULL is never a dead end — offer the one-tap raise.
            if (code === "CREW_FULL") {
                const crewSize = Number(body?.crewSize ?? 1);
                void confirmDialog({
                    title: "Crew is full",
                    message: `This job's crew size is ${crewSize}. Add them anyway and raise it to ${crewSize + 1}?`,
                    confirmLabel: `Add + raise to ${crewSize + 1}`,
                }).then((ok) => {
                    if (ok) addCrewMutation.mutate({ ...vars, force: true });
                });
                return;
            }
            toast.error(typeof body?.message === "string" ? body.message : "Could not add that tech to the job.");
        },
    });

    /** Quick-action + drop-choice: add a tech to the job's crew, in place. */
    const onJobAddCrew = useCallback(
        (jobId: string, techId: string) => {
            const tech = techs.find((t) => t.id === techId);
            const techName = tech
                ? `${tech.first_name ?? ""} ${tech.last_name ?? ""}`.trim() || tech.email || "tech"
                : "tech";
            // NB: no unregistered-LEAD warning here. That warning's own advice
            // is "pair them as a helper with a registered tech instead" — which
            // is exactly what adding them to the crew does. The lead still
            // signs and submits, so a crew member's city registration is not a
            // gate; firing it here would tell the office to do what they just did.
            addCrewMutation.mutate({ jobId, techId, _label: `${techName} added to this job's crew` });
        },
        [addCrewMutation, techs],
    );

    /**
     * 11/3 — take one added tech off a crew job, on the board. Same call as
     * the job page (`DELETE /api/jobs/:id/assignments/:userId`); the lead and
     * the job's time are untouched. Undo re-adds them with the same role and
     * points mode.
     */
    const removeCrewMutation = useMutation({
        mutationFn: ({
            jobId,
            userId,
        }: {
            jobId: string;
            userId: string;
            name: string;
            role: string;
            earningMode?: string;
        }) => api.delete(`/api/jobs/${jobId}/assignments/${userId}`),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job"] });
            toast.success(`${vars.name} is off this job`, {
                duration: 10_000,
                action: {
                    label: "Undo",
                    onClick: () => {
                        api.post(`/api/jobs/${vars.jobId}/assignments`, {
                            userId: vars.userId,
                            role: vars.role === "tech" ? "tech" : "helper",
                            ...(vars.earningMode === "full" ||
                            vars.earningMode === "split_even" ||
                            vars.earningMode === "mirror"
                                ? { earningMode: vars.earningMode }
                                : {}),
                            force: true,
                        })
                            .then(() => {
                                qc.invalidateQueries({ queryKey: ["dispatch"] });
                                qc.invalidateQueries({ queryKey: ["jobs"] });
                                toast.success(`${vars.name} is back on this job`);
                            })
                            .catch(() => toast.error("Couldn't put them back. Add them again from the job's menu."));
                    },
                },
            });
        },
        onError: (err) => {
            const body = err instanceof ApiError ? (err.body as Record<string, unknown> | undefined) : undefined;
            toast.error(
                typeof body?.message === "string" ? body.message : "Couldn't take that tech off the job. Try again.",
            );
        },
    });

    const onJobRemoveCrew = useCallback(
        (jobId: string, userId: string) => {
            const job = [...allScheduled, ...allUnscheduled].find((j) => j.id === jobId);
            const member = job ? crewMembers(job).find((m) => m.userId === userId) : undefined;
            removeCrewMutation.mutate({
                jobId,
                userId,
                name: member?.firstName ?? "That tech",
                role: member?.role ?? "tech",
                earningMode: member?.earningMode,
            });
        },
        [allScheduled, allUnscheduled, removeCrewMutation],
    );

    const handleDrop = useCallback(
        async (jobId: string, newTechId: string, newStart: Date, ineligibleReason?: string | null) => {
            const allKnown = [...allScheduled, ...allUnscheduled];
            const job = allKnown.find((j) => j.id === jobId);
            // No-op if the job is already on this tech at this exact time —
            // avoids a redundant PATCH when the dispatcher accidentally drops
            // back on the source row.
            if (job?.assigned_to === newTechId && job?.scheduled_start === newStart.toISOString()) {
                return;
            }
            const crewCount = 1 + (job?.crew?.length ?? 0);
            const crewNames = [job?.assigned_to_name, ...(job?.crew ?? []).map((c) => c.name)]
                .filter(Boolean)
                .join(", ");
            const dropTech = techs.find((t) => t.id === newTechId);
            const dropTechName = dropTech
                ? `${dropTech.first_name ?? ""} ${dropTech.last_name ?? ""}`.trim() || dropTech.email || "that tech"
                : "that tech";
            // Issue #1245 — dropping a job that ALREADY has a lead onto a
            // different tech's lane is genuinely ambiguous: the office means
            // either "move it to them" or "put them both on it". Guessing
            // "move" silently took the first tech off the job, which is how
            // two-man jobs kept vanishing. Ask instead.
            const alreadyOnJob =
                job?.assigned_to === newTechId || (job?.crew ?? []).some((c) => c.user_id === newTechId);
            let askedViaChoice = false;
            if (job && job.assigned_to && !alreadyOnJob) {
                askedViaChoice = true;
                const pick = await choiceDialog({
                    title: "Move it, or add them?",
                    message: `"${job.customer_name ?? job.title ?? "This job"}" is on ${job.assigned_to_name ?? "another tech"}.`,
                    choices: [
                        {
                            value: "add",
                            label: `Add ${dropTechName} to this job`,
                            description: `Both work it together at its current time. Stays on ${job.assigned_to_name ?? "the current tech"}'s day too; points split evenly.`,
                        },
                        {
                            value: "move",
                            label: `Move it to ${dropTechName}`,
                            description:
                                crewCount > 1
                                    ? `Takes it off the whole crew (${crewNames}) and reschedules for ${dropTechName}.`
                                    : `Takes it off ${job.assigned_to_name ?? "the current tech"}.`,
                        },
                    ],
                });
                if (pick === null) return;
                if (pick === "add") {
                    onJobAddCrew(jobId, newTechId);
                    return;
                }
            }
            // Flexible crew: a multi-tech job renders on every member's lane —
            // dragging ANY copy moves the whole crew. Confirm so a lane-local
            // reshuffle doesn't silently move three people's day. Skipped when
            // the choice dialog above already spelled out the crew move.
            if (job && crewCount > 1 && !askedViaChoice) {
                const ok = await confirmDialog({
                    message: `This job has a crew of ${crewCount} (${crewNames}). Moving it reschedules the job for the whole crew. Move it?`,
                    confirmLabel: "Move for all",
                });
                if (!ok) return;
            }
            const fromClusters: ClusterJob | undefined = (clustersQuery.data?.clusters ?? [])
                .flatMap((c) => c.jobs)
                .find((j) => j.id === jobId);
            // 30-min floor (G3 amendment): the dropped window is never
            // smaller than the minimum schedulable visit.
            const minutes = Math.max(
                MIN_VISIT_MINUTES,
                job?.estimated_duration_minutes ?? fromClusters?.estimatedDurationMinutes ?? DEFAULT_VISIT_MINUTES,
            );
            const end = new Date(newStart.getTime() + minutes * 60_000);
            const tech = techs.find((t) => t.id === newTechId);
            const techName = tech ? `${tech.first_name} ${tech.last_name}`.trim() : "tech";
            if (ineligibleReason) {
                toast.warning(`Override: ${techName} ${ineligibleReason.toLowerCase()}`, {
                    description: "Assignment applied — review skill or city access if this was unintentional.",
                });
            }
            const contactVerifiedAt =
                job?.site_contact_last_verified_at ?? fromClusters?.siteContactLastVerifiedAt ?? null;
            if (isContactStale(contactVerifiedAt)) {
                toast.warning("Site contact not verified", {
                    description: "Confirm the location contact is still current before the on-my-way text goes out.",
                });
            }
            // #1431: ask BEFORE the drop lands. A clean pairing never sees a
            // dialog; an override still opens the office-review flag on the
            // way out, exactly as it did when this was a toast afterwards.
            const decision = await askBeforeAssign(jobId, newTechId);
            if (!decision.proceed) return;
            // #1728 — new job page on: a dispatch of a job that isn't ready
            // for the tech asks once. A warning, not a gate.
            if (!(await confirmDispatch(jobId, job))) return;
            // Rich drop confirmation: name the customer, the tech, and the
            // snapped landing time so the dispatcher gets immediate feedback
            // that the job landed where they intended (vs. a generic toast).
            const customerLabel =
                job?.customer_name ?? fromClusters?.customerName ?? job?.title ?? fromClusters?.title ?? "Job";
            const whenLabel = newStart.toLocaleString([], {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            });
            patchJobMutation.mutate({
                jobId,
                assignedTo: newTechId,
                scheduledStart: newStart.toISOString(),
                scheduledEnd: end.toISOString(),
                status: "scheduled",
                ...(decision.reason ? { assignmentReason: decision.reason } : {}),
                _label: `Scheduled: ${customerLabel} → ${techName}, ${whenLabel}`,
                _dropTechName: techName,
                _before: job
                    ? { assigned_to: job.assigned_to, scheduled_start: job.scheduled_start, status: job.status }
                    : undefined,
            });
        },
        [
            allScheduled,
            allUnscheduled,
            clustersQuery.data,
            patchJobMutation,
            techs,
            askBeforeAssign,
            onJobAddCrew,
            confirmDispatch,
        ],
    );

    const handleUnschedule = useCallback(
        (jobId: string) => {
            patchJobMutation.mutate({
                jobId,
                assignedTo: null,
                scheduledStart: null,
                scheduledEnd: null,
                status: "awaiting_scheduling",
                _label: "Job unscheduled",
            });
        },
        [patchJobMutation],
    );

    // H24: assign a backlog job to a tech without committing a time. Leaves the
    // job unscheduled (no start/end) so it stays in the backlog but now owned by
    // a tech; the dispatcher can drop it onto the grid later to set the time.
    const handleBacklogAssign = useCallback(
        (jobId: string, techId: string) => {
            const tech = techs.find((t) => t.id === techId);
            const techName = tech ? `${tech.first_name} ${tech.last_name}`.trim() : "tech";
            patchJobMutation.mutate({
                jobId,
                assignedTo: techId,
                _label: `Assigned to ${techName}`,
            });
        },
        [patchJobMutation, techs],
    );

    // ─── Schedule dialog (explicit manual control) ─────────────────────
    const [scheduleTarget, setScheduleTarget] = useState<{
        job: DispatchJob;
        techId?: string | null;
        date?: string | null;
        time?: string | null;
    } | null>(null);

    const openScheduleDialog = useCallback(
        (jobId: string, opts?: { techId?: string | null; date?: string | null; time?: string | null }) => {
            let job: DispatchJob | undefined = [...allScheduled, ...allUnscheduled].find((j) => j.id === jobId);
            if (!job) {
                // Backlog cards come from the clusters feed (ClusterJob shape),
                // which isn't a full DispatchJob — synthesize the fields the
                // dialog needs so card-click can still open it.
                const cj = (clustersQuery.data?.clusters ?? []).flatMap((c) => c.jobs).find((j) => j.id === jobId);
                if (cj) {
                    job = {
                        id: cj.id,
                        title: cj.title,
                        status: cj.status,
                        customer_name: cj.customerName,
                        assigned_to: null,
                        assigned_to_name: null,
                        trade_name: null,
                        trade_code: null,
                        scheduled_start: null,
                        scheduled_end: null,
                        site_label: [cj.siteAddress, cj.siteName].filter(Boolean).join(" · ") || cj.siteName,
                        site_city: cj.city,
                        due_by_date: cj.dueByDate,
                        // Surface the job-type duration model + device count so the
                        // dialog shows a real estimate for a not-yet-scheduled job.
                        estimated_duration_minutes: cj.estimatedDurationMinutes ?? null,
                        asset_count: cj.deviceCount ?? null,
                    } as DispatchJob;
                }
            }
            if (!job) return;
            // Date default: the dialog itself falls back to the job's existing
            // scheduled day (reschedule path). For a backlog job (no scheduled
            // start) there's nothing to fall back to, so seed the currently
            // viewed grid day — the common case is "build out the day on screen".
            const dateDefault = opts?.date ?? (job.scheduled_start ? null : selectedDate);
            setScheduleTarget({
                job,
                techId: opts?.techId ?? null,
                date: dateDefault,
                time: opts?.time ?? null,
            });
        },
        [allScheduled, allUnscheduled, clustersQuery.data, selectedDate],
    );

    // Week-board drop handler. The week cells have no intra-day resolution, so
    // instead of silently committing a hardcoded 9:00 start we open the
    // Schedule dialog pre-filled with the dropped tech + day, letting the
    // dispatcher confirm/adjust the time before it lands.
    const handleWeekScheduleDrop = useCallback(
        (jobId: string, techId: string, dateKey: string) => {
            openScheduleDialog(jobId, { techId, date: dateKey });
        },
        [openScheduleDialog],
    );

    // ─── Quick-action handlers (job-block context menu) ────────────────
    //
    // All route through `patchJobMutation` so the existing onSuccess
    // invalidation refreshes every dispatch + dashboard surface. Reschedules
    // keep the job's wall-clock time (shifting only the calendar day) and its
    // duration; reassign keeps the time and only swaps the tech.
    //
    // IMPORTANT: "tomorrow" / "next week" are computed from TODAY's real date,
    // not the job's currently-scheduled day. A dispatcher looking at a job
    // sitting two days ago expects "Move to tomorrow" to mean literal tomorrow,
    // not "the day after that job's old slot". The menu labels show the
    // resulting date so there's no ambiguity.
    const rescheduleJobByDays = useCallback(
        (jobId: string, days: number, label: string) => {
            const allKnown = [...allScheduled, ...allUnscheduled];
            const job = allKnown.find((j) => j.id === jobId);
            if (!job) return;

            const newKey = addDaysToDateKey(localDateKey(new Date()), days);
            const [y, m, d] = newKey.split("-").map((n) => parseInt(n, 10));
            // Keep the job's existing wall-clock time; fall back to 9am if it
            // has never been scheduled.
            let hh = 9;
            let mm = 0;
            const base = job.scheduled_start ? new Date(job.scheduled_start) : null;
            if (base && !Number.isNaN(base.getTime())) {
                hh = base.getHours();
                mm = base.getMinutes();
            }
            const newStart = new Date(y, (m ?? 1) - 1, d ?? 1, hh, mm, 0, 0);

            const minutes = visitMinutesFromJob(job);
            const end = new Date(newStart.getTime() + minutes * 60_000);
            patchJobMutation.mutate({
                jobId,
                scheduledStart: newStart.toISOString(),
                scheduledEnd: end.toISOString(),
                status: "scheduled",
                _label: label,
            });
        },
        [allScheduled, allUnscheduled, patchJobMutation],
    );

    // Resulting-date labels for the relative shortcuts, derived from TODAY so
    // they always read literally ("Move to tomorrow (Wed Jun 17)").
    const { moveToTomorrowLabel, pushNextWeekLabel } = useMemo(() => {
        const fmt = (key: string): string => {
            const [y, m, d] = key.split("-").map((n) => parseInt(n, 10));
            const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
            return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        };
        const todayKey = localDateKey(new Date());
        return {
            moveToTomorrowLabel: `Move to tomorrow (${fmt(addDaysToDateKey(todayKey, 1))})`,
            pushNextWeekLabel: `Push to next week (${fmt(addDaysToDateKey(todayKey, 7))})`,
        };
    }, []);

    const onJobMoveToTomorrow = useCallback(
        (jobId: string) => rescheduleJobByDays(jobId, 1, "Moved to tomorrow"),
        [rescheduleJobByDays],
    );

    const onJobPushNextWeek = useCallback(
        (jobId: string) => rescheduleJobByDays(jobId, 7, "Pushed to next week"),
        [rescheduleJobByDays],
    );

    const onScheduleSubmit = useCallback(
        async (args: ScheduleSubmit) => {
            const tech = techs.find((t) => t.id === args.assignedTo);
            const tName = tech
                ? `${tech.first_name ?? ""} ${tech.last_name ?? ""}`.trim() || tech.email || "tech"
                : "tech";
            const job = [...allScheduled, ...allUnscheduled].find((j) => j.id === args.jobId);
            const customer = job?.customer_name || "Job";
            const when = new Date(args.scheduledStart);
            const dateStr = when.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
            const timeStr = when.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
            // #1728 — the same soft pre-flight as a drop.
            if (!(await confirmDispatch(args.jobId, job))) return;
            patchJobMutation.mutate({
                jobId: args.jobId,
                assignedTo: args.assignedTo,
                scheduledStart: args.scheduledStart,
                scheduledEnd: args.scheduledEnd,
                status: "scheduled",
                _label: `Scheduled: ${customer} → ${tName}, ${dateStr} ${timeStr}`,
            });
        },
        [patchJobMutation, techs, allScheduled, allUnscheduled, confirmDispatch],
    );

    // "Move to specific date…" — keeps the job's tech + wall-clock time and
    // only changes the calendar day. Inline prompt mirrors onJobAdjustTime so
    // we don't introduce a second modal for a one-field edit.
    const onJobMoveToSpecificDate = useCallback(
        async (jobId: string) => {
            const allKnown = [...allScheduled, ...allUnscheduled];
            const job = allKnown.find((j) => j.id === jobId);
            if (!job) return;
            const base = job.scheduled_start ? new Date(job.scheduled_start) : null;
            const baseValid = base != null && !Number.isNaN(base.getTime());
            const defaultKey = baseValid ? localDateKey(base!) : selectedDate;
            const input = await promptDialog({
                title: "Move to date",
                label: "Date (YYYY-MM-DD)",
                defaultValue: defaultKey,
            });
            if (input == null) return;
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
            if (!match) {
                toast.error("Enter a valid date as YYYY-MM-DD.");
                return;
            }
            const y = Number(match[1]);
            const mo = Number(match[2]);
            const d = Number(match[3]);
            let hh = 9;
            let mm = 0;
            if (baseValid) {
                hh = base!.getHours();
                mm = base!.getMinutes();
            }
            const newStart = new Date(y, mo - 1, d, hh, mm, 0, 0);
            if (Number.isNaN(newStart.getTime())) {
                toast.error("Enter a valid date as YYYY-MM-DD.");
                return;
            }
            const minutes = visitMinutesFromJob(job);
            const end = new Date(newStart.getTime() + minutes * 60_000);
            const needsSchedule = job.status === "draft" || job.status === "awaiting_scheduling";
            patchJobMutation.mutate({
                jobId,
                scheduledStart: newStart.toISOString(),
                scheduledEnd: end.toISOString(),
                ...(needsSchedule ? { status: "scheduled" } : {}),
                _label: "Moved to date",
            });
        },
        [allScheduled, allUnscheduled, patchJobMutation, selectedDate],
    );

    const onJobReassign = useCallback(
        async (jobId: string, techId: string) => {
            const tech = techs.find((t) => t.id === techId);
            const techName = tech
                ? `${tech.first_name ?? ""} ${tech.last_name ?? ""}`.trim() || tech.email || "tech"
                : "tech";
            const decision = await askBeforeAssign(jobId, techId);
            if (!decision.proceed) return;
            patchJobMutation.mutate({
                jobId,
                assignedTo: techId,
                ...(decision.reason ? { assignmentReason: decision.reason } : {}),
                _label: `Assigned to ${techName}`,
            });
        },
        [patchJobMutation, techs, askBeforeAssign],
    );

    // "Adjust time" quick-action — keeps the job on its current calendar day
    // (or the selected day when unscheduled) and only moves its wall-clock
    // start, recomputing the end from the existing duration. A lightweight
    // prompt keeps this inline without a new modal; it routes through the same
    // patchJobMutation as every other reschedule.
    const onJobAdjustTime = useCallback(
        async (jobId: string) => {
            const allKnown = [...allScheduled, ...allUnscheduled];
            const job = allKnown.find((j) => j.id === jobId);
            if (!job) return;
            const base = job.scheduled_start ? new Date(job.scheduled_start) : null;
            const baseValid = base != null && !Number.isNaN(base.getTime());
            const defaultTime = baseValid
                ? `${String(base!.getHours()).padStart(2, "0")}:${String(base!.getMinutes()).padStart(2, "0")}`
                : "09:00";
            const input = await promptDialog({
                title: "Adjust start time",
                label: "New start time (24-hour, HH:MM)",
                defaultValue: defaultTime,
            });
            if (input == null) return;
            const match = /^(\d{1,2}):(\d{2})$/.exec(input.trim());
            const hh = match ? Number(match[1]) : NaN;
            const mm = match ? Number(match[2]) : NaN;
            if (!match || hh > 23 || mm > 59) {
                toast.error("Enter a valid time as HH:MM (24-hour).");
                return;
            }
            const dateKey = baseValid ? localDateKey(base!) : selectedDate;
            const [y, mo, d] = dateKey.split("-").map((n) => parseInt(n, 10));
            const newStart = new Date(y, (mo ?? 1) - 1, d ?? 1, hh, mm, 0, 0);
            const minutes = visitMinutesFromJob(job);
            const end = new Date(newStart.getTime() + minutes * 60_000);
            const needsSchedule = job.status === "draft" || job.status === "awaiting_scheduling";
            patchJobMutation.mutate({
                jobId,
                scheduledStart: newStart.toISOString(),
                scheduledEnd: end.toISOString(),
                ...(needsSchedule ? { status: "scheduled" } : {}),
                _label: "Time adjusted",
            });
        },
        [allScheduled, allUnscheduled, patchJobMutation, selectedDate],
    );

    // "Message tech" quick-action — best-effort. There's no per-tech deep
    // link in the messaging surface today, so we land the dispatcher on the
    // shared messages inbox rather than inventing a route.
    const onJobMessageTech = useCallback(
        (_jobId: string) => {
            navigate("/messages");
        },
        [navigate],
    );

    // "Mark cannot complete" quick-action (H23) — opens a confirmation dialog
    // that requires a reason and routes through POST /jobs/:id/cannot-complete
    // (records the reason, audits, sends to Office Review). Replaces the old
    // instant, reasonless one-click skip.
    // #1143 — confirm/unconfirm a booking from the Grid. The board already
    // READS `dispatch_lock_state` to draw the lock pill; the only way to SET
    // it was the job page's Booking card, so the dispatcher opened a job for
    // every confirmation and navigated back. Same endpoint that card uses, so
    // there is one write path rather than two that can drift.
    const toggleConfirmMutation = useMutation({
        mutationFn: ({ jobId, confirm }: { jobId: string; confirm: boolean }) =>
            confirm ? api.post(`/api/jobs/${jobId}/confirm`) : api.delete(`/api/jobs/${jobId}/confirm`),
        onSuccess: (_data, vars) => {
            // Mirror patchJobMutation's refresh set — the lock pill is drawn
            // from the dispatch queries, and the job page may be open behind.
            qc.invalidateQueries({ queryKey: ["dispatch"] });
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            qc.invalidateQueries({ queryKey: ["job"] });
            toast.success(vars.confirm ? "Booking confirmed" : "Booking unconfirmed");
        },
        onError: (err) => {
            const body = err instanceof ApiError ? (err.body as Record<string, unknown> | null) : null;
            toast.error(typeof body?.message === "string" ? body.message : "Couldn't change the booking confirmation");
        },
    });
    const onJobToggleConfirm = useCallback(
        (jobId: string, confirm: boolean) => toggleConfirmMutation.mutate({ jobId, confirm }),
        [toggleConfirmMutation],
    );

    const onJobSkip = useCallback((jobId: string) => setSkipJobId(jobId), []);

    // "Cancel job" quick-action — opens the reason dialog (required reason for
    // non-admins). The dialog handles the POST + cache invalidation.
    const onJobCancel = useCallback((jobId: string) => setCancelJobId(jobId), []);

    // ─── Missed-jobs triage actions ────────────────────────────────────
    //
    // These all flow through `patchJobMutation` (and one extra POST for
    // auto-fill) so the existing onSuccess invalidation already refreshes
    // every dispatch + dashboard surface. Tech-picker times default to 9am
    // local on the chosen calendar day; office can drag to a precise slot
    // if they need a different time.
    const buildScheduledRange = useCallback(
        (isoStart: string, durationMinutes: number): { start: string; end: string } => {
            const start = new Date(isoStart);
            const end = new Date(start.getTime() + durationMinutes * 60_000);
            return { start: start.toISOString(), end: end.toISOString() };
        },
        [],
    );

    const moveMissedToDay = useCallback(
        (job: MissedJob, isoStart: string, techId: string) => {
            const allKnown = [...allScheduled, ...allUnscheduled];
            const known = allKnown.find((j) => j.id === job.id);
            const minutes = Math.max(MIN_VISIT_MINUTES, known?.estimated_duration_minutes ?? DEFAULT_VISIT_MINUTES);
            const { start, end } = buildScheduledRange(isoStart, minutes);
            const tech = techs.find((t) => t.id === techId);
            const techName = tech
                ? `${tech.first_name ?? ""} ${tech.last_name ?? ""}`.trim() || tech.email || "tech"
                : "tech";
            patchJobMutation.mutate({
                jobId: job.id,
                assignedTo: techId,
                scheduledStart: start,
                scheduledEnd: end,
                status: "scheduled",
                _label: `Rescheduled to ${techName}`,
            });
        },
        [allScheduled, allUnscheduled, buildScheduledRange, patchJobMutation, techs],
    );

    const autoFillMissed = useCallback(
        async (job: MissedJob) => {
            // Auto-schedule eligibility requires `assigned_to IS NULL AND
            // scheduled_start IS NULL` (see dispatchAutoSchedule.ts), so
            // we clear the slot first and then ask the scheduler to slot
            // it in. Status flips back to scheduled if the apply lands a
            // time, otherwise the job sits in `awaiting_scheduling`.
            try {
                await api.patch(`/api/jobs/${job.id}`, {
                    assignedTo: null,
                    scheduledStart: null,
                    scheduledEnd: null,
                    status: "awaiting_scheduling",
                });
                const result = await api.post<{ applied?: number; totals?: { daysWithProposals?: number } }>(
                    "/api/dispatch/auto-schedule",
                    {
                        jobIds: [job.id],
                        apply: true,
                    },
                );
                qc.invalidateQueries({ queryKey: ["dispatch"] });
                qc.invalidateQueries({ queryKey: ["dashboard", "command-summary"] });
                qc.invalidateQueries({ queryKey: ["dashboard", "missed-jobs"] });
                qc.invalidateQueries({ queryKey: ["jobs"] });
                if (!result?.applied || result.applied === 0) {
                    toast.success("Sent to auto-scheduler queue", {
                        description: `${job.title} is now in Ready for Dispatch.`,
                    });
                } else {
                    toast.success("Auto-scheduled", {
                        description: `${job.title} placed by the auto-scheduler.`,
                    });
                }
            } catch (e) {
                console.error("[missed auto-fill]", e);
                toast.error("Auto-fill failed — please try again");
            }
        },
        [qc],
    );

    const sendMissedToOfficeReview = useCallback(
        (job: MissedJob, reason: string) => {
            // Reuses the existing PATCH route so we hit the same audit
            // trail as the nightly flagger. cannot_complete_reason is
            // intentionally not surfaced through PATCH /jobs (no field
            // for it in the existing UI flow), so we leave it empty
            // here — office will see the textual reason in the review
            // queue once they pick this up.
            void reason;
            patchJobMutation.mutate({
                jobId: job.id,
                status: "office_review",
                _label: "Sent to office review",
            });
        },
        [patchJobMutation],
    );

    // C10: route the missed-job cancel through the guarded cancel endpoint
    // (POST /jobs/:id/cancel) instead of a raw PATCH status:'cancelled'. The
    // endpoint enforces the sent-invoice guard, records the reason, and writes
    // the proper audit fields — the PATCH path skipped all of that.
    const cancelMissedJob = useCallback(
        (job: MissedJob, reason: string) => {
            const trimmed = reason.trim();
            api.post<{ open_purchase_orders?: CancelledJobPo[] }>(`/api/jobs/${job.id}/cancel`, {
                reason: trimmed || undefined,
            })
                .then((data) => {
                    toast.success("Job cancelled");
                    // #1684: a cancelled job's orders don't stop by themselves.
                    const poNotice = cancelledJobPoNotice(data?.open_purchase_orders);
                    if (poNotice) {
                        toast.warning(poNotice.message, {
                            duration: 20_000,
                            action: { label: "Open PO", onClick: () => navigate(`/purchasing?po=${poNotice.poId}`) },
                        });
                        qc.invalidateQueries({ queryKey: ["purchase-orders"] });
                    }
                    qc.invalidateQueries({ queryKey: ["dispatch"] });
                    qc.invalidateQueries({ queryKey: ["jobs"] });
                    qc.invalidateQueries({ queryKey: ["job", job.id] });
                })
                .catch((err: unknown) => {
                    const body = err instanceof ApiError ? (err.body as any) : null;
                    const msg =
                        body?.message ??
                        (body?.error === "INVOICE_SENT"
                            ? "This job has an invoice that has already been issued, so it can no longer be cancelled."
                            : null) ??
                        "Cancel failed";
                    toast.error(msg);
                });
        },
        [qc, navigate],
    );

    const missedJobActions = useMemo(
        () => ({
            onAutoFill: (job: MissedJob) => {
                void autoFillMissed(job);
            },
            onMoveToToday: (job: MissedJob, techId: string) => {
                const isoStart = new Date(`${selectedDate}T09:00:00`).toISOString();
                moveMissedToDay(job, isoStart, techId);
            },
            onMoveToTomorrow: (job: MissedJob, techId: string) => {
                const tomorrow = addDaysToDateKey(selectedDate, 1);
                const isoStart = new Date(`${tomorrow}T09:00:00`).toISOString();
                moveMissedToDay(job, isoStart, techId);
            },
            onPickDateTime: (job: MissedJob, isoStart: string, techId: string) => {
                moveMissedToDay(job, isoStart, techId);
            },
            onSendToOfficeReview: sendMissedToOfficeReview,
            onCancel: cancelMissedJob,
            onOpenJob: (job: MissedJob) => navigate(`/jobs/${job.id}`),
        }),
        [autoFillMissed, cancelMissedJob, moveMissedToDay, navigate, selectedDate, sendMissedToOfficeReview],
    );

    // Smooth scroll into view when arriving from the dashboard banner.
    useEffect(() => {
        if (searchParams.get("section") === "missed") {
            const target = document.getElementById("missed-jobs-section");
            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [searchParams, missedJobsQuery.data]);

    const isLoadingDay = techsQuery.isLoading || scheduledQuery.isLoading || unscheduledQuery.isLoading;

    const openDayFromWeek = useCallback(
        (dateKey: string) => {
            setSelectedDate(dateKey);
            const sp = new URLSearchParams(searchParams);
            sp.set("view", "day");
            sp.set("date", dateKey);
            setSearchParams(sp, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    const filteredScheduled = useMemo(() => {
        if (focusMode === "risk") {
            return allScheduled.filter((j) => {
                const p = pulseByJob.get(j.id);
                return p && (p.risk === "at_risk" || p.risk === "overdue");
            });
        }
        if (focusMode === "live") {
            return allScheduled.filter(
                (j) => j.status === "in_progress" || j.status === "arrived" || j.status === "en_route",
            );
        }
        return allScheduled;
    }, [focusMode, allScheduled, pulseByJob]);

    const filteredTechJobMap = useMemo(() => {
        if (!focusMode || focusMode === "dispatch" || focusMode === "scheduled") return techJobMap;
        const map = new Map<string, DispatchJob[]>();
        for (const tech of techs) map.set(tech.id, []);
        for (const job of filteredScheduled) {
            if (!job.assigned_to) continue;
            const list = map.get(job.assigned_to);
            if (list) list.push(job);
            else map.set(job.assigned_to, [job]);
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
        }
        return map;
    }, [focusMode, filteredScheduled, techJobMap, techs]);

    const backlogExpanded = focusMode === "dispatch";

    // Only the "live"/"risk" focus modes actually drop scheduled jobs off the
    // technician timeline; "dispatch"/"scheduled" leave the full map intact.
    // Surface the hidden count so an empty-looking swimlane is never mistaken
    // for "nothing is scheduled today."
    const hiddenByFocus =
        focusMode === "live" || focusMode === "risk" ? Math.max(0, allScheduled.length - filteredScheduled.length) : 0;

    const focusStageHex = focusMode
        ? getStage(
              focusMode === "dispatch"
                  ? "ready_to_schedule"
                  : focusMode === "live"
                    ? "in_progress"
                    : focusMode === "risk"
                      ? "at_risk"
                      : ("scheduled" as LifecycleStageId),
          ).hexColor
        : "#000000";

    // ── #1766 new Grid: what the day strip and board read ──────────────────
    const newGridDay = newGrid.on && mode === "day";
    // 11/3: say which Grid is drawn on every write, for the audit log.
    useEffect(() => {
        setGridUiVersion(newGridDay ? "new" : "classic");
        return () => setGridUiVersion(null);
    }, [newGridDay]);
    const [showNudge, setShowNudge] = useState(false);
    const jobsById = useMemo(() => {
        const m = new Map<string, DispatchJob>();
        for (const j of allUnscheduled) m.set(j.id, j);
        for (const j of allScheduled) m.set(j.id, j);
        return m;
    }, [allScheduled, allUnscheduled]);
    const newGridTally = useMemo(
        () => stateTally(allScheduled, (id) => pulseByJob.get(id)?.risk),
        [allScheduled, pulseByJob],
    );
    const newGridNeeds = useMemo(
        () => ({
            missed: leftOpenOn ? (leftOpenQuery.data?.counts.total ?? 0) : (missedJobsQuery.data?.counts.total ?? 0),
            late: isTodaySelected ? (pulseQuery.data?.counts.atRisk ?? 0) + (pulseQuery.data?.counts.overdue ?? 0) : 0,
            noTime: readinessQuery.data?.unslotted.length ?? 0,
            noTech: unassignedScheduled.length,
            notOnMap: missingCoordsCount,
            pastDue: allScheduled.filter((j) => !isCancelled(j) && isPastDue(j, selectedDate)).length,
        }),
        [
            missedJobsQuery.data,
            leftOpenOn,
            leftOpenQuery.data,
            isTodaySelected,
            pulseQuery.data,
            readinessQuery.data,
            unassignedScheduled,
            missingCoordsCount,
            allScheduled,
            selectedDate,
        ],
    );
    const pairedHelperCount = useMemo(() => {
        let n = 0;
        for (const list of helpersByLead.values()) n += list.length;
        return n;
    }, [helpersByLead]);
    const jumpToNewGridJob = useCallback(
        (kind: "noTime" | "noTech") => {
            const id = kind === "noTime" ? readinessQuery.data?.unslotted[0]?.jobId : unassignedScheduled[0]?.id;
            const el = id ? document.querySelector(`[data-job-id="${id}"]`) : null;
            el?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
            if (id) {
                setHighlightJobId(id);
                window.setTimeout(() => setHighlightJobId((cur) => (cur === id ? null : cur)), HIGHLIGHT_MS);
            }
        },
        [readinessQuery.data, unassignedScheduled],
    );

    const clearFocus = () => {
        setFocusMode(null);
        clearContext();
    };

    return (
        <div className="space-y-3">
            {/* Teach cards (#1838): one at a time — the new Grid, then Left open. */}
            <TeachIntro featureKey="grid.new" when={newGrid.on} />
            <TeachIntro featureKey="left-open.office" when={leftOpenOn} />
            {/* Office: gold dots on Grid buttons never used (max 3). */}
            <TeachDots />
            {focusMode && (
                <div
                    className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 text-sm"
                    style={{ borderColor: focusStageHex + "55", backgroundColor: focusStageHex + "12" }}
                >
                    <span className="flex items-center gap-1.5 font-semibold">
                        <Filter className="h-4 w-4" />
                        Filtered to “{FOCUS_LABELS[focusMode]}”
                    </span>
                    {hiddenByFocus > 0 && (
                        <span className="text-foreground/80">
                            {hiddenByFocus} scheduled {hiddenByFocus === 1 ? "job is" : "jobs are"} hidden from the
                            timeline below.
                        </span>
                    )}
                    <button
                        type="button"
                        className="ml-auto inline-flex items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-semibold transition-colors hover:bg-muted"
                        onClick={clearFocus}
                    >
                        <X className="h-3.5 w-3.5" />
                        Show all jobs
                    </button>
                </div>
            )}
            {/* ── G6.1 workspace: queue dock (left rail) + board column. The
                board column carries the pinned command bar, then the triage
                bar (one slim row), then the lanes — so the first tech lane
                stays above the fold. ── */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                {/* RETURN NEEDED + AWAITING DISPATCH live in the dock now
                    (G6.1) — same data, same drag-onto-tech wiring. */}
                <QueueDock
                    techs={techs}
                    targetDate={selectedDate}
                    backlogCollapsed={mode === "day" && backlogExpanded ? false : autoEnabled}
                    showReadiness={newGrid.on && can(PERMISSIONS.officeReview.manage)}
                    techSkills={techSkills}
                    jobSkillReqs={jobSkillReqs}
                    techCityCerts={techCityCerts}
                    jobCityRestriction={jobCityRestriction}
                    onJobClick={(id) => navigate(`/jobs/${id}`)}
                    onScheduleJob={openScheduleDialog}
                    onJobDragStart={setDraggingJobId}
                    onJobDragEnd={() => setDraggingJobId(null)}
                    onUnscheduleJob={handleUnschedule}
                    onCancelJob={onJobCancel}
                    onAssignJob={handleBacklogAssign}
                    onRescheduleReturn={(job) => setScheduleTarget({ job, techId: null, date: null, time: null })}
                />

                <div className="min-w-0 flex-1 space-y-3">
                    {/* Everything from the old header row lives here now; the
                        Automation cluster renders only with dispatch.manage. */}
                    <CommandBar
                        selectedDate={selectedDate}
                        onSelectDate={setSelectedDate}
                        mode={mode}
                        onModeChange={setMode}
                        trades={trades}
                        tradeFilter={tradeFilter}
                        onTradeFilterChange={setTradeFilter}
                        fullDayView={fullDayView}
                        onToggleFullDay={() => setFullDayView((v) => !v)}
                        fitDay={fitDay}
                        onToggleFitDay={() => setFitDay((v) => !v)}
                        counters={{
                            ready: allUnscheduled.length,
                            scheduledToday: allScheduled.length,
                            inProgress: inProgressCount,
                            needsAttention: needsAttentionCount,
                            completed: completedCount,
                            techsOnDuty,
                            techsTotal: techs.length,
                        }}
                        isToday={isTodaySelected}
                        showOutToday={can(PERMISSIONS.dispatch.view)}
                        attentionNotes={attentionNotes}
                        onAttentionClick={openTriageDetail}
                        canAutomate={canManageDispatch}
                        autoSchedule={{
                            enabled: autoEnabled,
                            loading: autoSettingsQuery.isLoading,
                            toggling: toggleAutoScheduleMutation.isPending,
                            lastRunAt: autoSettingsQuery.data?.lastRunAt ?? null,
                            intervalMinutes: autoSettingsQuery.data?.runIntervalMinutes ?? 60,
                            lastRunSummary: autoSettingsQuery.data?.lastRunSummary,
                            onToggle: (next) => toggleAutoScheduleMutation.mutate(next),
                        }}
                        onAutoFill={() => {
                            autoScheduleMutation.mutate();
                            setAutoScheduleOpen(true);
                        }}
                        onSuggestMoves={() => {
                            rebalanceMutation.mutate();
                            setRebalanceOpen(true);
                        }}
                        newGrid={{ on: newGrid.on, onChange: newGrid.set }}
                        quiet={newGridDay}
                        live={{
                            ...liveDispatch,
                            // "updated N s ago" = the board's last successful read, any reason.
                            lastReadAt: Math.max(scheduledQuery.dataUpdatedAt, unscheduledQuery.dataUpdatedAt) || null,
                        }}
                    />

                    {!newGridDay && <GridAttentionBar />}

                    {newGridDay && (
                        <NewGridDayStrip
                            tally={newGridTally}
                            techsWorking={techsOnDuty}
                            techsTotal={techs.length}
                            helperCount={pairedHelperCount}
                            needs={newGridNeeds}
                            freeHelpers={benchHelpers}
                            draggingHelperId={draggingHelperId}
                            onHelperDragStart={setDraggingHelperId}
                            onHelperDragEnd={() => setDraggingHelperId(null)}
                            onBenchDrop={handleBenchDrop}
                            onHelperRevert={handleHelperRevert}
                            onOpenTriage={openTriageDetail}
                            onJumpToJob={jumpToNewGridJob}
                        />
                    )}

                    {/* On the new Grid the triage detail opens from a
                        Needs-you chip instead of sitting here as a strip. */}
                    {(!newGridDay || triageExpanded) && (
                        <TriageAlertBar
                            id="dispatch-triage-bar"
                            mode={mode}
                            pulse={pulseQuery.data}
                            pulseLoading={pulseQuery.isLoading}
                            pulseFetching={pulseQuery.isFetching}
                            onPulseRefresh={() => pulseQuery.refetch()}
                            missed={missedJobsQuery.data}
                            techs={techs}
                            missedActions={missedJobActions}
                            missedSectionId="missed-jobs-section"
                            missingCoordsCount={missingCoordsCount}
                            expanded={triageExpanded}
                            onExpandedChange={setTriageExpanded}
                            showMissedAutoFill={canManageDispatch}
                            leftOpen={leftOpenQuery.data}
                        />
                    )}

                    {/* Shadow-mode nudge (Issue #592) — suggest-only, admin-gated, day board. */}
                    {mode === "day" && canManageDispatch && (!newGridDay || showNudge) ? (
                        <NudgeCard date={selectedDate} />
                    ) : null}

                    {mode === "day" ? (
                        <div className="space-y-4">
                            {/* #763: board-top legend — decodes rings, repair
                                treatment, device codes, paper cities, points. */}
                            {!newGridDay && <GridLegend />}
                            {isLoadingDay ? (
                                <div className="flex h-64 items-center justify-center rounded-lg border bg-card">
                                    <PageSpinner />
                                </div>
                            ) : techsQuery.isError ? (
                                <div className="flex h-64 items-center justify-center rounded-lg border bg-card">
                                    <div className="text-center space-y-1">
                                        <CalendarDays className="mx-auto h-8 w-8 text-foreground/80" />
                                        <p className="text-foreground/80 font-medium">Could not load technicians.</p>
                                        <p className="text-xs text-foreground/80">
                                            Refresh the page. If this persists, contact an admin.
                                        </p>
                                    </div>
                                </div>
                            ) : techs.length === 0 ? (
                                <div className="flex h-64 items-center justify-center rounded-lg border bg-card">
                                    <div className="text-center space-y-1">
                                        <CalendarDays className="mx-auto h-8 w-8 text-foreground/80" />
                                        <p className="text-foreground/80 font-medium">
                                            No jobs scheduled for this date.
                                        </p>
                                        {/* techs.length === 0 here means the org has no Technician-role
                                        users yet, so the setup hint is genuinely relevant. */}
                                        <p className="text-xs text-foreground/80">
                                            Add technicians in Admin &gt; Users to start dispatching.
                                        </p>
                                    </div>
                                </div>
                            ) : newGridDay ? (
                                <HighlightJobProvider jobId={highlightJobId}>
                                    <NewDayBoard
                                        techs={gridTechs}
                                        techJobMap={filteredTechJobMap}
                                        selectedDate={selectedDate}
                                        isToday={isTodaySelected}
                                        pulseByJob={pulseByJob}
                                        outByTech={outByTech}
                                        helpersByLead={helpersByLead}
                                        unassignedJobs={unassignedScheduled}
                                        unslotted={readinessQuery.data?.unslotted ?? []}
                                        jobsById={jobsById}
                                        onDrop={handleDrop}
                                        onJobClick={(id) => navigate(`/jobs/${id}`)}
                                        draggingJobId={draggingJobId}
                                        onJobDragStart={setDraggingJobId}
                                        onJobDragEnd={() => setDraggingJobId(null)}
                                        onOutClick={setOutActionUserId}
                                        onMarkOut={openMarkOut}
                                        draggingHelperId={draggingHelperId}
                                        onHelperDragStart={setDraggingHelperId}
                                        onHelperDragEnd={() => setDraggingHelperId(null)}
                                        onHelperDrop={handleHelperDrop}
                                        onTechDrop={handleTechDrop}
                                        onHelperRevert={handleHelperRevert}
                                        techJobTypeApprovals={techJobTypeApprovals}
                                        jobJobTypeId={jobJobTypeId}
                                        jobTypeLabelById={jobTypeLabelById}
                                        techCityCerts={techCityCerts}
                                        jobCityRestriction={jobCityRestriction}
                                        onSuggestFit={canManageDispatch ? () => setShowNudge(true) : undefined}
                                        fullDayView={fullDayView}
                                        fitDay={fitDay}
                                        onShowAllHours={() => {
                                            setFitDay(false);
                                            setFullDayView(true);
                                        }}
                                        actions={{
                                            onAdjustTime: onJobAdjustTime,
                                            onReassign: onJobReassign,
                                            onMoveToSpecificDate: onJobMoveToSpecificDate,
                                            onMoveToTomorrow: onJobMoveToTomorrow,
                                            onPushNextWeek: onJobPushNextWeek,
                                            onUnschedule: handleUnschedule,
                                            onToggleConfirm: onJobToggleConfirm,
                                            onAddCrew: onJobAddCrew,
                                            onRemoveCrew: onJobRemoveCrew,
                                            onMessageTech: onJobMessageTech,
                                            onSkip: onJobSkip,
                                            onCancel: onJobCancel,
                                            moveToTomorrowLabel,
                                            pushNextWeekLabel,
                                        }}
                                    />
                                </HighlightJobProvider>
                            ) : (
                                <>
                                    {hiddenByFocus > 0 && (
                                        <div
                                            className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs text-foreground/80"
                                            style={{ borderColor: focusStageHex + "55" }}
                                        >
                                            <Filter className="h-3.5 w-3.5 shrink-0" />
                                            <span>
                                                Showing{" "}
                                                <span className="font-semibold">{FOCUS_LABELS[focusMode!]}</span> only —{" "}
                                                {hiddenByFocus} scheduled {hiddenByFocus === 1 ? "job" : "jobs"} hidden
                                                from the timeline. The routes below still show every planned stop.
                                            </span>
                                            <button
                                                type="button"
                                                className="font-semibold underline underline-offset-2 transition-colors hover:text-foreground"
                                                onClick={clearFocus}
                                            >
                                                Show all
                                            </button>
                                        </div>
                                    )}
                                    {/* Helper bench — unattached helpers sit here,
                                    visible and draggable; dropping a chip here
                                    writes an explicit solo-today override. */}
                                    <HelperBench
                                        helpers={benchHelpers}
                                        hasHelpers={(pairingsQuery.data?.pairings.length ?? 0) > 0}
                                        draggingHelperId={draggingHelperId}
                                        onDragStart={setDraggingHelperId}
                                        onDragEnd={() => setDraggingHelperId(null)}
                                        onRevert={handleHelperRevert}
                                        onBenchDrop={handleBenchDrop}
                                        onMarkOut={() => openMarkOut()}
                                        onOutClick={(h) => setOutActionUserId(h.id)}
                                    />
                                    <HighlightJobProvider jobId={highlightJobId}>
                                        <DaySwimlane
                                            techs={gridTechs}
                                            techJobMap={filteredTechJobMap}
                                            selectedDate={selectedDate}
                                            fullDayView={fullDayView}
                                            fitDay={fitDay}
                                            isToday={isTodaySelected}
                                            unslottedByTech={unslottedByTech}
                                            onJobClick={(id) => navigate(`/jobs/${id}`)}
                                            onJobEdit={(id) => navigate(`/jobs/${id}/edit`)}
                                            onDrop={handleDrop}
                                            pulseByJob={pulseByJob}
                                            techSkills={techSkills}
                                            jobSkillReqs={jobSkillReqs}
                                            techJobTypeApprovals={techJobTypeApprovals}
                                            jobJobTypeId={jobJobTypeId}
                                            jobTypeLabelById={jobTypeLabelById}
                                            techCityCerts={techCityCerts}
                                            jobCityRestriction={jobCityRestriction}
                                            restrictedCityLabels={restrictedCityLabels}
                                            helpersByLead={helpersByLead}
                                            outByTech={outByTech}
                                            onMarkOut={openMarkOut}
                                            onOutClick={setOutActionUserId}
                                            draggingHelperId={draggingHelperId}
                                            onHelperDragStart={setDraggingHelperId}
                                            onHelperDragEnd={() => setDraggingHelperId(null)}
                                            onHelperDrop={handleHelperDrop}
                                            onHelperRevert={handleHelperRevert}
                                            onTechDrop={handleTechDrop}
                                            draggingJobId={draggingJobId}
                                            onJobDragStart={setDraggingJobId}
                                            onJobDragEnd={() => setDraggingJobId(null)}
                                            unassignedJobs={unassignedScheduled}
                                            onJobSchedule={openScheduleDialog}
                                            onJobMoveToTomorrow={onJobMoveToTomorrow}
                                            onJobPushNextWeek={onJobPushNextWeek}
                                            onJobReassign={onJobReassign}
                                            onJobAddCrew={onJobAddCrew}
                                            onJobRemoveCrew={onJobRemoveCrew}
                                            onJobAdjustTime={onJobAdjustTime}
                                            onJobMoveToSpecificDate={onJobMoveToSpecificDate}
                                            onJobUnschedule={handleUnschedule}
                                            onJobMessageTech={onJobMessageTech}
                                            onJobSkip={onJobSkip}
                                            onJobCancel={onJobCancel}
                                            onJobToggleConfirm={onJobToggleConfirm}
                                            moveToTomorrowLabel={moveToTomorrowLabel}
                                            pushNextWeekLabel={pushNextWeekLabel}
                                        />
                                    </HighlightJobProvider>
                                </>
                            )}
                            {!newGridDay && unassignedToday.length > 0 && (
                                <div className="rounded-lg border border-amber-300/50 bg-amber-50/50 dark:bg-amber-900/10 p-3 space-y-2">
                                    <h3 className="text-sm font-semibold flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                                        <AlertTriangle className="h-4 w-4" />
                                        Unassigned Today ({unassignedToday.length})
                                    </h3>
                                    <div className="grid gap-1.5">
                                        {unassignedToday.map((j) => (
                                            <div
                                                key={j.id}
                                                className="flex items-center justify-between rounded-md border bg-background px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigate(`/jobs/${j.id}`)}
                                                draggable
                                                onDragStart={(e) => {
                                                    // PRE-CUTOVER #38: the swimlane / backlog / missed-jobs all
                                                    // read the dragged job id from the "text/plain" MIME slot.
                                                    // Earlier this code used "jobId" which the swimlane never
                                                    // reads, so dragging a row out of "Unassigned Today" onto a
                                                    // tech column silently no-op'd (job stayed unassigned even
                                                    // though it visually moved). Keep this in sync with the
                                                    // other drag sources.
                                                    e.dataTransfer.setData("text/plain", j.id);
                                                    e.dataTransfer.effectAllowed = "move";
                                                    setDraggingJobId(j.id);
                                                }}
                                                onDragEnd={() => setDraggingJobId(null)}
                                            >
                                                <div className="min-w-0">
                                                    <div className="font-medium truncate">{j.title}</div>
                                                    <div className="text-xs text-foreground/80 truncate">
                                                        {j.customer_name}
                                                        {j.site_label ? ` · ${j.site_label}` : ""}
                                                    </div>
                                                </div>
                                                <div className="text-xs text-foreground/80 shrink-0 ml-2">
                                                    {j.scheduled_start
                                                        ? new Date(j.scheduled_start).toLocaleTimeString([], {
                                                              hour: "numeric",
                                                              minute: "2-digit",
                                                          })
                                                        : "No time"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Routes by technician — always render so dispatchers
                            can see today's planned stops regardless of which
                            focus mode (Ready for Dispatch / Scheduled / etc.)
                            they entered the page from. */}
                            <BackfillCoordinatesBanner routes={techRoutesQuery.data?.routes ?? []} />
                            <RouteRail
                                techs={techs}
                                routes={techRoutesQuery.data?.routes ?? []}
                                shop={techRoutesQuery.data?.shop ?? null}
                                loading={techRoutesQuery.isLoading}
                                suggestionsByTech={suggestionsByTech}
                                onExpandTech={(techId) => navigate(`/dispatch/map?date=${selectedDate}&tech=${techId}`)}
                            />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* #828 M8: the legend rides into week view too — it
                                used to vanish exactly where the codes still show. */}
                            <GridLegend />
                            <WeekHeatmap
                                selectedDate={selectedDate}
                                onOpenDay={openDayFromWeek}
                                onScheduleDrop={handleWeekScheduleDrop}
                            />
                        </div>
                    )}
                </div>
            </div>

            <RebalanceDialog
                open={rebalanceOpen}
                onClose={() => setRebalanceOpen(false)}
                date={selectedDate}
                response={rebalanceMutation.data}
                isLoading={rebalanceMutation.isPending}
            />
            <AutoScheduleDialog
                open={autoScheduleOpen}
                onClose={() => setAutoScheduleOpen(false)}
                fromDate={selectedDate}
                response={autoScheduleMutation.data}
                isLoading={autoScheduleMutation.isPending}
            />
            {cancelJobId && (
                <CancelJobDialog
                    open={!!cancelJobId}
                    onClose={() => setCancelJobId(null)}
                    jobId={cancelJobId}
                    jobLabel={[...allScheduled, ...allUnscheduled].find((j) => j.id === cancelJobId)?.title ?? null}
                    onCancelled={() => setCancelJobId(null)}
                />
            )}
            {skipJobId && (
                <SkipJobDialog
                    open={!!skipJobId}
                    onClose={() => setSkipJobId(null)}
                    jobId={skipJobId}
                    jobLabel={[...allScheduled, ...allUnscheduled].find((j) => j.id === skipJobId)?.title ?? null}
                    onDone={() => setSkipJobId(null)}
                />
            )}
            {staleConflict && (
                <StaleJobDialog
                    open
                    body={staleConflict.body}
                    attempt={staleConflict.attempt}
                    onShowMe={showMeStale}
                    onMoveAnyway={moveAnywayStale}
                />
            )}
            <ScheduleJobDialog
                open={!!scheduleTarget}
                onClose={() => setScheduleTarget(null)}
                job={scheduleTarget?.job ?? null}
                techs={techs}
                initialTechId={scheduleTarget?.techId ?? null}
                initialDate={scheduleTarget?.date ?? null}
                initialTime={scheduleTarget?.time ?? null}
                onSchedule={onScheduleSubmit}
            />
            <OutDayDialog
                open={outDialogOpen}
                onClose={() => setOutDialogOpen(false)}
                people={outDayPeople}
                defaultUserId={outDialogUserId}
                defaultDate={selectedDate}
            />
            <OutDayActionsDialog target={outActionTarget} onClose={() => setOutActionUserId(null)} />
            {pendingChipDrop && (
                <ChipDropPopover
                    target={pendingChipDrop}
                    onScope={handleChipScope}
                    onCancel={() => setPendingChipDrop(null)}
                    hideDefault={pendingDropIsTech}
                />
            )}
            {/* #1431 — "Before you assign this". Renders nothing until a drop
                is actually about to go wrong. */}
            {assignmentDialog}
        </div>
    );
}

/**
 * Banner above the route rail that surfaces a one-click backfill when
 * any of today's stops still lack lat/lng. The dispatcher's first
 * instinct is to assume "the map is broken" — naming the count and
 * offering a direct fix is much friendlier than "1 stop without
 * coordinates" buried inside a route card.
 *
 * Hidden when every stop is mapped, so this stays out of the way once
 * the geocoder has caught up.
 */
function BackfillCoordinatesBanner({ routes }: { routes: TechRoute[] }) {
    const qc = useQueryClient();
    const unmapped = routes.reduce((acc, r) => acc + r.jobs.filter((j) => j.unmapped).length, 0);
    const mutation = useMutation({
        mutationFn: () =>
            api.post<{ total: number; geocoded: number; skipped: number; failed: number }>(
                "/api/sites/geocode-backfill",
                {},
            ),
        onSuccess: (res) => {
            if (res.geocoded === 0 && res.total === 0) {
                toast.success("No sites needed coordinates.");
            } else if (res.geocoded === 0) {
                toast.warning(
                    `Geocoder couldn't pin any of the ${res.total} sites missing coordinates. Check DISPATCH_GEOCODE_PROVIDER and your user agent.`,
                );
            } else {
                toast.success(
                    `Geocoded ${res.geocoded} of ${res.total} sites${res.failed > 0 ? ` (${res.failed} failed, ${res.skipped} skipped)` : ""}.`,
                );
            }
            qc.invalidateQueries({ queryKey: ["dispatch-tech-routes"] });
        },
        onError: (e: unknown) => {
            const msg = e instanceof Error ? e.message : "Failed to backfill coordinates";
            toast.error(msg);
        },
    });
    if (unmapped === 0) return null;
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200">
            <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>
                    {unmapped} stop{unmapped === 1 ? "" : "s"} missing coordinates — they won't show up on the route map
                    until geocoded.
                </span>
            </div>
            <Button size="sm" variant="outline" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
                <MapPin className="mr-1.5 h-3.5 w-3.5" />
                {mutation.isPending ? "Backfilling…" : "Backfill site coordinates"}
            </Button>
        </div>
    );
}
